/*
 * KAI particle orb engine.
 *
 * Business Ops import boundary: this file is the secret-free, self-hosted copy
 * of the newest deployed SeenStage DESKTOP engine at commit
 * afd219657d5da4343dbcbdee8ae9e4940d524a12. Its donor SHA-256 is
 * c50efe9943857711704d78da757df505dd2e87c454d102428eb9927339c56818.
 * Motion/state math stays donor-owned; the only visual adaptation below maps
 * KAI cyan to Luna red. SeenStage runtime, sessions, data and credentials are
 * not imported or contacted.
 *
 * Adapted for Seenstage/KAI from Reezxy/Jarvis---Local-Voice-assistant
 * frontend/src/orb.ts, which is MIT licensed.
 *
 * Private testing note 2026-06-11: the motion bar is cross-checked against
 * ethanplusai/jarvis, whose README/source target the Jarvis "audio-reactive
 * particle orb straight out of the MCU" feel. Do not treat that repo as a
 * public/commercial-cleared dependency until the license is resolved or this
 * private reference is replaced.
 *
 * WHY 2026-06-20 ORB-PF-62 GPU shader recovery:
 * The CPU `THREE.Points` + per-frame proximity-line lattice + rigid whole-object
 * rotation/jitter read as a "see-through spinning line ball" (ORB-PF-59), shook
 * the whole body instead of vibrating (ORB-PF-60), pinned glow constantly on
 * (ORB-PF-61), and janked on phone when per-particle JS life was added
 * (ORB-PF-55, reverted e1252bb5/49278843). Those are STRUCTURAL properties of the
 * primitives, not tuning misses. This pass makes the particle field the body,
 * computed per-particle on the GPU via a custom ShaderMaterial (independent
 * twinkle/wave/tight-vibration as vertex-shader work — one draw call, cheap on
 * phone), demotes the line lattice to a faint accent, amplitude-gates the glow,
 * and keeps spin as one secondary channel. The proven channel/audio state machine
 * and the public API/dataset/callback contracts are preserved unchanged so the
 * startup (L2), voice (L3), room/focus (L4), and caption (L5) lanes stay isolated.
 *
 * MIT License
 *
 * Copyright (c) 2026 Reezxy
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import * as THREE from "three";
import {
  OPS_ORB_IDLE_RADIUS_SCALE,
  OPS_ORB_PEAK_RADIUS_SCALE,
  nextOpsOrbOutputVisualLevel,
  opsOrbAudioEnvelope,
  opsOrbRadiusScale
} from "./ops-orb-audio-envelope.js";
import { createOpsOrbAnimationFrameLease } from "./ops-orb-canvas-lease.js";
import { selectKaiOrbMotionAuthority } from "./ops-seenstage-orb-motion-authority.js";

export type KaiParticleOrbState = "idle" | "listening" | "thinking" | "speaking";

export type KaiParticleOrbQuality = "mobile" | "desktop";
export type KaiOrbAnalyserTiming = "input" | "output";

export type KaiParticleOrbOptions = {
  quality?: KaiParticleOrbQuality;
  pixelRatio?: number;
  reducedMotion?: boolean;
  onFatalError?: (error: Error) => void;
  onAmplitudeFrame?: (state: {
    audiblePcm: boolean;
    amplitudeLevel: number;
    loudnessMultiplier: number;
    pulseScale: number;
    vibration: number;
    vibrationRate: number;
    shake: number;
    flare: number;
    bodyJitter: number;
	    coreGlow: number;
	    lineGlow: number;
	    particleBreath: number;
	    microTwinkle: number;
	    sparkleEnergy: number;
	    microVariance: number;
	    bass: number;
	    mid: number;
	    treble: number;
	    rms: number;
	    peak: number;
	    radiusScale: number;
	  }) => void;
	};

export type KaiParticleOrbEngine = {
  setState(state: KaiParticleOrbState): void;
  setAnalyser(analyser: AnalyserNode | null, timing?: KaiOrbAnalyserTiming): void;
  setReducedMotion(reduced: boolean): void;
  setPaused(paused: boolean): void;
  resize(): void;
  captureFrameDataUrlForProof(): string;
  destroy(): void;
};

type Electron = {
  sx: number;
  sy: number;
  sz: number;
  ex: number;
  ey: number;
  ez: number;
  t: number;
  speed: number;
};

type Connection = {
  x1: number;
  y1: number;
  z1: number;
  x2: number;
  y2: number;
  z2: number;
};

// WHY 2026-06-14 Orb camera margin: page-level CSS owns how large the Orb
// appears in the studio composition. The WebGL camera keeps internal margin so
// speech ripples and electron rails have room inside the transparent canvas.
// WHY [BOL-ORB-DESKTOP-PROJECTION-002]: the close 128 camera made the 88px Ops
// stage a nearly full red disk and clipped the very expansion the user needs to
// read as sound. Restore the deployed desktop projection and give its canvas
// donor-sized overscan in CSS. The calibrated radius below keeps idle readable
// at ~23px while a real Aoede crest can expand past 70px without becoming haze.
// Falsifier: idle fills the stage, peaks clip, or mobile selects another camera.
const CAMERA_Z = 272;
const LINE_DISTANCE = 8;
// WHY [BOL-ORB-ROUNDED-KNOWLEDGE-SCAN-013]: c431 multiplied X by ~3.2 while
// compressing Y/Z below 0.5, so the shared Orb became the exact thin red bar in
// the installed Samsung frame. Keep the accepted spherical volume, but bound it
// to the proven 3.25 speaking crest: the c439 mobile scan painted ~92px across
// an 82px slot at 3.65 and visibly crossed the controls. Falsifier: Thinking is
// flatter than 0.78 height/width, leaves the slot, or loses its dense motion.
const OPS_ORB_SCAN_RADIUS_SCALE = 3.25;
// WHY [BOL-ORB-DENSE-FINE-FIELD-005]: More points must read as a denser sphere,
// not consume more translucent fill or become large blobs at speech peaks.
// Counts rise ~60% while point diameter falls to 76%, keeping aggregate sprite
// area slightly below c295. Falsifier: the field stays sparse, individual points
// grow coarse, or phone frame pacing regresses under native Aoede output.
const OPS_ORB_POINT_DIAMETER_SCALE = 0.76;
// WHY [BOL-ORB-FINE-PARTICLE-RASTER-FLOOR-006]: c296 correctly increased the
// field and reduced sprite diameter, but the literal Samsung crop showed that
// most added points fell below one WebGL raster pixel and disappeared. Keep the
// 0.76 small-particle scale and give preselected readable cores a slightly wider
// compact sprite. The sprite's transparent aperture keeps the visible dot near
// one physical pixel. Falsifier: idle remains sparse, becomes a solid disk, the
// visible cores become coarse, or phone frame pacing regresses.
const OPS_ORB_READABLE_POINT_FLOOR_CSS_PX = 3.6;
// WHY [BOL-ORB-ALL-POINT-RASTER-FLOOR-010]: literal c299 Samsung proof showed
// 3,400 allocated points but only the readable-core subset survived rasterizing,
// so the body still looked like a few pale specks. A floor below one physical
// pixel is not a drawable particle: literal SwiftShader proof at 0.38–0.9 CSS
// px retained only 22–42 pixels from the entire field. The donor's soft texture
// puts its bright core inside the middle 14% of the sprite, so a 1–2 physical-px
// sprite samples only the transparent tail. Give compact sprites enough raster
// area to sample that tiny core; their visible dot remains about one physical
// pixel. This reveals fine density without adding vertices or broadening glow.
const OPS_ORB_BASE_POINT_FLOOR_CSS_PX = 3;
// WHY [BOL-ORB-COMPACT-RESOLUTION-007]: a 1.5x WebGL surface was compositor-
// enlarged onto Samsung's 3.5x screen, merging fine particles into pale clumps.
// Render this tiny bounded stage at up to 2.25x so sub-CSS-pixel points stay
// distinct. The canvas is only ~161 CSS pixels, so literal phone pacing—not a
// generic mobile downgrade—owns acceptance. Falsifier: points still blur/sparsify
// or native Aoede playback pushes frame pacing outside the accepted budget.
const OPS_ORB_COMPACT_MAX_PIXEL_RATIO = 2.25;
// WHY 2026-06-20 ORB-PF-62 shader body scale: the shader displaces particles from
// their rest radius for breath/wave/vibration/pulse/jump. Keep the idle base used
// for the radius->scale uniform so speech can visibly grow the body.
const BASE_RADIUS = 15;

// WHY 2026-06-20 ORB-PF-62 GPU vertex shader: every per-particle channel
// (breath, traveling wave/glisten, independent twinkle, tight high-frequency
// vibration, rapid pulse, jump-scale) is computed here in parallel on the GPU
// instead of in a JS loop over thousands of particles. Spin stays a demoted
// Object3D rotation (applied on the mesh), so this shader runs in local space.
// `position` is the rest position; `color` is the linear base palette (read by
// the canonical shader body).
const ORB_VERTEX_SHADER = /* glsl */ `
  attribute float aPhase;
  attribute float aLayer;     // 0 = deep interior ... 1 = outer shell
  attribute float aSizeSeed;  // per-particle base size variance
  attribute float aReadableCore; // bounded mobile idle core strength
  attribute float aConnectionNode; // stable endpoint in the current thinking pattern
  attribute vec3 color;       // explicit for the canonical ShaderMaterial body

  uniform float uTime;
  uniform float uBreath;
  uniform float uWave;
  uniform vec3  uWaveDir;
  uniform float uTwinkle;
  uniform float uVibration;
  uniform float uVibRate;
  uniform float uPulse;
  uniform float uPulsePhase;
  uniform float uJumpScale;
  uniform float uRadiusScale;
	  uniform float uCoreGlow;
  uniform float uMicroTwinkle;
  uniform float uSparkleEnergy;
  uniform float uMicroVariance;
  uniform float uIdleReadabilityLift;
  uniform float uIdleDepthCue;
  uniform float uRimSpin;
  uniform float uActiveMotion;
  uniform float uScanBlend;
  uniform float uScanTravel;
  uniform float uScanScale;
  uniform float uConnectionMorph;
  uniform float uSize;
  uniform float uPointDiameterScale;
  uniform float uBasePointFloorCssPx;
  uniform float uReadablePointFloorCssPx;
  uniform float uScale;
  uniform float uPixelRatio;
  uniform float uCameraZ;
  uniform float uDepthRange;

  varying vec3 vColor;
  varying float vGlow;
  varying float vDepth;
  varying float vSurfaceLight;
  varying float vTwinkle;
  varying float vReadableCore;
  varying float vRimSpin;
  varying float vScanEnergy;

  void main() {
    vec3 pos = position;
    float radius = length(pos) + 1e-4;
    vec3 dir = pos / radius;
    // WHY 2026-07-30 audible-PCM phase continuity:
    // Never multiply absolute time by an activity switch. Doing so teleports every
    // oscillator to an unrelated phase when speech starts or stops. Phases advance
    // continuously; the small displacement outputs are weighted instead.
    float motionTime = uTime;
    float activeMotion = clamp(uActiveMotion, 0.0, 1.0);

    // Breath: slow low-frequency radial in/out of the whole field. Present at
    // idle too (kept alive, ORB-PF-17/29) and swells while speaking (ORB-PF-34).
    // One coherent phase survives every state edge. Audible PCM owns the extent;
    // this slow breath only keeps the particle body alive between words.
    float breath = sin(uTime * 1.35);
    pos += dir * breath * uBreath * (0.55 + aLayer * 0.85);

    // Wave / glisten: a traveling surface wave independent of rigid rotation, so
    // the shell shimmers even when spin is near zero (ORB-PF-59 wave/glisten).
    float wave = sin(motionTime * 3.4 + dot(dir, uWaveDir) * 4.0 + aPhase);
    pos += dir * wave * uWave * (0.4 + aLayer);
    // WHY 2026-06-21 ORB-PF-80 3D ripple repair:
    // The user called out that the live body still felt like a flat spinning
    // shell. Add subtle tangent + camera-depth displacement tied to the same
    // wave so the particles ripple through the volume, not only radially.
    vec3 tangent = cross(dir, normalize(vec3(0.31, 0.77, 0.55)));
    if (length(tangent) < 0.001) tangent = cross(dir, vec3(0.0, 1.0, 0.0));
    tangent = normalize(tangent);
	    float depthRipple = sin(motionTime * 2.8 + dir.z * 6.2 + aPhase * 0.7);
	    pos += tangent * wave * uWave * (0.12 + aLayer * 0.22);
	    pos.z += depthRipple * uWave * (0.18 + aLayer * 0.34);

	    // WHY 2026-06-21 ORB-PF-90/92:
	    // The accepted older Orb felt alive because tiny particles glistened and
	    // phased independently. Keep that as a separate shader-side lane so a future
	    // spin/radius/glow patch cannot erase the micro-life while tests still pass.
	    vec3 tangentB = normalize(cross(tangent, dir));
	    // WHY 2026-07-30 PF-19 active-only rim repair:
	    // Shader-local rim travel still reads as rotation at rest even when the
	    // Object3D transform is fixed. Keep the accepted rim articulation for
	    // speech/thinking/demo, but let uActiveMotion freeze it at idle/listening.
	    float rimBand = smoothstep(0.58, 1.0, aLayer);
	    float rimA = sin(motionTime * (2.1 + uRimSpin * 0.7) + aPhase * 0.37 + dir.y * 3.4);
	    float rimB = cos(motionTime * (1.55 + uRimSpin * 0.45) + aPhase * 0.23 + dir.x * 2.8);
	    pos += (tangent * rimA + tangentB * rimB * 0.42) * uRimSpin * rimBand * (0.34 + aReadableCore * 0.16);
	    float microRate = 1.0 + uSparkleEnergy * 1.35 + uVibRate * 0.12;
	    float microA = sin(motionTime * (13.0 + aSizeSeed * 17.0) * microRate + aPhase * 2.17 + dir.z * 5.1);
	    float microB = cos(motionTime * (19.0 + aLayer * 11.0) * microRate + aPhase * 1.31 + dir.x * 4.3);
	    float microTravel = activeMotion * uMicroTwinkle * (0.05 + uMicroVariance * 0.095) * (0.35 + aLayer * 0.65);
	    pos += (tangent * microA + tangentB * microB) * microTravel;
	    pos += dir * sin(motionTime * 31.0 * microRate + aPhase * 0.91) * microTravel * uMicroVariance * 0.42;

    // Tight per-particle vibration: high frequency, SMALL travel, ramped by
    // amplitude rate. Per-particle (never a whole-object offset) so it reads as a
    // buzz in place, not a broad shake (ORB-PF-60 / ORB-PF-33 double-fast-half-far).
    float vibHz = motionTime * 150.0 * uVibRate;
    vec3 vib = vec3(
      sin(vibHz + aPhase * 1.7),
      cos(vibHz * 0.92 + aPhase * 2.1),
      sin(vibHz * 1.1 + aPhase * 0.9)
    );
    pos += vib * uVibration;

    // Rapid pulse: in/out beat distinct from breath (ORB-PF-09 / ORB-PF-11). The
    // phase is accumulated frame-rate-independently in JS (uPulsePhase).
    pos += dir * sin(uPulsePhase + aPhase * 0.2) * uPulse;

    // Jump-scale + amplitude radius: whole-orb swell on loud syllables (ORB-PF-11
    // jump toward viewer).
    pos *= uRadiusScale * (1.0 + uJumpScale);

    // WHY [BOL-ORB-SHARED-SCAN-001]: truthful smart work uses this same point
    // field—not a second spinner or overlay. Between audible packets it fans to
    // the accepted maximum speaking width and combines co-prime directional and
    // radial phases, so the shape reads as an active scan without visibly
    // repeating. Real output PCM rapidly releases uScanBlend and restores the
    // untouched amplitude-owned speech body. Falsifier: Thinking stays a tiny
    // ball, snaps with no transition, repeats one mechanical loop, or changes
    // particle size/palette/renderer when audio begins.
    float scanBlend = smoothstep(0.0, 1.0, clamp(uScanBlend, 0.0, 1.0));
    float scanTravel = clamp(uScanTravel, 0.18, 1.0);
    vec3 scanBase = position * uScanScale;
    float scanRadius = length(scanBase.xy);
    float scanSweep = sin(
      scanBase.x * 0.165 - motionTime * 6.17 + aPhase * 0.013
    );
    float scanRing = cos(
      scanRadius * 0.29 - motionTime * 2.31 + aPhase * 0.029
    );
    float scanCross = sin(
      scanBase.x * 0.071 + scanBase.y * 0.083 + motionTime * 1.47 + aPhase * 0.021
    );
    vec3 scanNormal = normalize(scanBase + vec3(0.0001));
    vec3 scanTangent = cross(scanNormal, normalize(vec3(0.23, 0.67, 0.71)));
    if (length(scanTangent) < 0.001) scanTangent = cross(scanNormal, vec3(0.0, 1.0, 0.0));
    scanTangent = normalize(scanTangent);
    float scanEnergy = scanBlend * clamp(
      pow(0.5 + 0.5 * scanSweep, 5.0) * 0.72
        + pow(0.5 + 0.5 * scanRing, 7.0) * 0.28,
      0.0,
      1.0
    );
    vec3 scanTarget = scanBase
      + scanNormal * (scanSweep * 1.28 + scanRing * 0.84) * scanTravel
      + scanTangent * scanCross * 0.62 * scanTravel
      + vec3(0.0, scanCross * 0.28, scanRing * 0.42) * scanTravel;
    // Selected dots physically settle onto the same rest-space endpoints used
    // by the CPU line buffer. Pattern ownership changes only while this morph is
    // zero, so points form/dissolve the schematic instead of lines jumping over
    // an unrelated moving cloud.
    float connectionNodeMorph = smoothstep(
      0.0,
      1.0,
      clamp(aConnectionNode * uConnectionMorph, 0.0, 1.0)
    );
    scanTarget = mix(scanTarget, scanBase, connectionNodeMorph);
    pos = mix(pos, scanTarget, scanBlend);

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

	    // Independent twinkle: per-particle size + brightness oscillation seeded by
	    // aPhase, so small particles sparkle on their own (ORB-PF-52/56). Free on GPU.
	    float tw = 0.5 + 0.5 * sin(motionTime * (4.0 + aSizeSeed * 6.0) + aPhase * 6.2831);
	    float sparkle = 0.5 + 0.5 * sin(motionTime * (16.0 + aSizeSeed * 10.0) * (1.0 + uSparkleEnergy * 0.9) + aPhase * 3.1 + aLayer * 4.0);
    // WHY 2026-07-31 zero-idle-sparkle + fine-particle separation:
    // The first idle repair continuously blended every particle toward the same
    // medium size/brightness as amplitude fell. That removed the accepted tiny
    // and disappearing points and produced the user's large, uniform "blobs."
    // Use uActiveMotion only as a binary visibility lease: exact rest remains
    // neutral, while any audible PCM restores the full seeded size/brightness
    // range. The centering term must use this same binary lease: centering with
    // the continuous activeMotion value adds one shared positive size offset to
    // all 2,500 points at low/mid PCM, making fine particles overlap into the
    // exact giant blobs this repair protects against. Do not cap point size or
    // retune count/seeds/DPR—the accepted multiscale field depends on them.
    float visibleMotion = step(0.001, activeMotion);
    float motionTwinkle = mix(0.5, tw, visibleMotion);
    float motionSparkle = sparkle * visibleMotion;
    float readableCore = uIdleReadabilityLift * aReadableCore;
    // WHY [BOL-ORB-PARTICLE-DEPTH-014]: transparent point sprites deliberately
    // keep depth writes off; enabling a depth buffer would punch hard holes
    // through their soft apertures. Use the existing view-space depth plus a
    // fixed radial key light to separate far/interior, shell, and near particles
    // in the same crimson field. This changes no vertices, count, renderer, or
    // broad glow. Falsifier: the Orb still reads as a flat dot sheet, changes hue,
    // or loses the accepted dense particle body.
    float viewDepth = -mvPosition.z;
    vDepth = clamp((uCameraZ - viewDepth) / uDepthRange + 0.5, 0.0, 1.0);
    vSurfaceLight = 0.5 + 0.5 * dot(dir, normalize(vec3(-0.36, 0.52, 0.77)));
    float depthCue = clamp(uIdleDepthCue, 0.0, 1.0);
    float depthFocus = mix(0.90, 1.14, smoothstep(0.06, 0.94, vDepth));
    vTwinkle = clamp(max(motionTwinkle, motionSparkle * uMicroTwinkle) + readableCore * 0.28, 0.0, 1.45);
	    float sizePx =
      uPointDiameterScale *
      uSize *
      aSizeSeed *
      (1.0 + uTwinkle * (motionTwinkle - 0.5) * 1.7 + uSparkleEnergy * (motionSparkle - 0.42 * visibleMotion) * 0.62) *
      (1.0 + readableCore * 1.95) *
      mix(1.0, depthFocus, depthCue);
    gl_PointSize = sizePx * (1.0 + scanEnergy * 0.48) * (uScale / max(0.001, -mvPosition.z));
    float readablePointFloor = mix(
      uBasePointFloorCssPx,
      uReadablePointFloorCssPx,
      step(0.001, aReadableCore)
    ) * uPixelRatio;
    gl_PointSize = max(gl_PointSize, readablePointFloor);
    // Mobile gl_PointSize cap guard (ALIASED_POINT_SIZE_RANGE is small on phones).
    gl_PointSize = clamp(gl_PointSize, 0.0, 72.0 * uPixelRatio);

    // Camera-facing depth: front particles catch ice-white light, rear sink into
    // deep cyan (ORB-PF-13 volumetric read, computed every frame for free here).
    // Interior particles light up from within when core glow rises (ORB-PF-28/36).
	    float interior = 1.0 - aLayer;
    float idleInterior = depthCue * interior * max(0.0, 1.0 - abs(vDepth - 0.5) * 1.7);
    vGlow =
      1.0 +
      uTwinkle * (motionTwinkle - 0.5) * 0.9 +
      uSparkleEnergy * motionSparkle * 0.38 +
      uCoreGlow * interior * 1.5 +
      readableCore * 0.56 +
      idleInterior * 0.24 +
      scanEnergy * 0.58;
    vReadableCore = readableCore;
    vRimSpin = rimBand * uRimSpin;
    vScanEnergy = scanEnergy;
    vColor = color;
  }
`;

// WHY 2026-06-20 ORB-PF-62 GPU fragment shader + r152+ color management:
// three r160+ renamed `encodings_fragment` to `colorspace_fragment` and a custom
// ShaderMaterial MUST do its own output color-space conversion. We author colors
// in linear working space (matching how the old PointsMaterial vertex-color path
// fed raw floats), output gl_FragColor, then `#include <colorspace_fragment>` to
// convert linear -> output sRGB. We deliberately do NOT include
// `premultiplied_alpha_fragment`: the renderer uses constructor
// `premultipliedAlpha:true` (canvas compositing) but the MATERIAL is
// non-premultiplied (default), exactly like the prior PointsMaterial, so adding
// the premultiply chunk would double-darken the edges and change the look.
const ORB_FRAGMENT_SHADER = /* glsl */ `
  uniform sampler2D uMap;
  uniform float uOpacity;
  uniform float uIdleReadabilityLift;
  uniform float uIdleDepthCue;
  uniform float uActiveMotion;

  varying vec3 vColor;
  varying float vGlow;
  varying float vDepth;
  varying float vSurfaceLight;
  varying float vTwinkle;
  varying float vReadableCore;
  varying float vRimSpin;
  varying float vScanEnergy;

  void main() {
    // Only the soft sprite's alpha falloff is used (its RGB color space is
    // irrelevant), so no decode is needed for the texture sample.
    float spriteAlpha = texture2D(uMap, gl_PointCoord).a;
    float mask = pow(spriteAlpha, 2.05);
    // WHY 2026-06-30 readable-core aperture repair:
    // The mobile idle Orb body/rim proof was spherical enough, but readable
    // core seeds stayed needle-thin and failed luma/bright-pixel gates. Widen
    // only their sprite aperture instead of adding particles, DOM glow, or
    // changing the protected rim/vibration/wave/ripple/shake motion lanes.
    float coreMask = pow(spriteAlpha, 3.05);
    float rimMask = pow(spriteAlpha, 4.15);
    float alpha =
      mask * uOpacity * (0.7 + vTwinkle * 0.5) * (1.0 + uIdleReadabilityLift * 0.08 + vReadableCore * 0.82) +
      coreMask * uOpacity * vReadableCore * 0.96 +
      rimMask * uOpacity * vRimSpin * 0.18;
    float depthCue = clamp(uIdleDepthCue, 0.0, 1.0);
    float idleFront = smoothstep(0.12, 0.88, vDepth);
    float surfaceKey = smoothstep(0.1, 0.9, vSurfaceLight);
    float depthAlpha = mix(0.96, 1.30, idleFront) * mix(0.94, 1.08, surfaceKey);
    alpha *= mix(1.0, depthAlpha, depthCue) * (1.0 + vScanEnergy * 0.34);
    // WHY 2026-06-29 ORB crisp-particle detail repair:
    // The mobile amplitude repair lowered glow/size correctly, but the broad
    // sprite tail still filled late speech as a smooth low-detail mist. Discard
    // the faint tail earlier so speech resolves as separate crisp points without
    // making the Orb larger or restoring a constant glow.
    // WHY 2026-06-29 active-speaking detail proof repair:
    // Mobile no-preference proof showed the speech body was visible but too
    // diffuse: lots of cyan fill, too few crisp luma jumps. Sharpen the alpha
    // mask and discard more of the tail so the same particles read as distinct
    // glints instead of a soft haze.
    // WHY 2026-06-29 mobile active-speaking point-density repair:
    // The halo clamp alone still left early speech frames as a broad cyan wash.
    // Spend the alpha budget on crisp particle cores and drop the soft tail, then
    // add mobile point count below; this raises real in-canvas glint edges instead
    // of weakening proof thresholds or bringing back line/electron lattices.
    // WHY 2026-06-29 mobile active-speaking crisp-particle repair:
    // After the proof crop stopped counting transcript UI, mobile speech still read
    // as cyan mist with too few crisp luma jumps. Tighten the sprite alpha tail
    // again so extra mobile particles render as separated glints, not smooth fog.
    if (alpha < 0.045) discard;

    // WHY 2026-06-20 ORB-PF-67 idle color-depth repair:
    // The first shader proof passed vibration/glow but the idle body averaged
    // >206 luma because front particles were mixed halfway to near-white ice.
    // Keep the highlight tint cyan and restrained at idle; speech vGlow can
    // still lift loud syllables, but rest/idle must keep darker volumetric depth
    // instead of reverting to cheap white strokes.
    // Business Ops keeps the exact desktop particle/aperture/depth mechanism,
    // but Luna's accepted identity is crimson rather than KAI cyan.
    // WHY [BOL-ORB-CRIMSON-LUMINANCE-001]: a literal RGB channel swap made the
    // accepted cyan body nearly black because red contributes far less perceived
    // luminance. Rotate hue into warm crimson while preserving the donor's light/
    // depth relationship. Falsifier: the moving points collapse into a dark disk.
    // WHY [BOL-ORB-DEEP-CRIMSON-CONTRAST-008]: the physical c296/c297 crops
    // proved the former warm highlight washed the tiny field into pale pink on
    // the Ops paper background. Saturate the point sprites themselves; never
    // compensate with a page-filling red halo. Falsifier: points remain faint,
    // lose depth, or merge into a flat dark disk.
    // WHY [BOL-ORB-DEEP-CRIMSON-CONTRAST-012]: full-opacity warm red still
    // composited as pale pink on Ops paper. Keep highlights in the same crimson
    // hue at lower linear luminance so individual dots—not a halo—carry contrast.
    vec3 ice = vec3(0.50, 0.005, 0.012);
    vec3 deep = vColor * vec3(0.72, 0.54, 0.58);
    // WHY 2026-06-30 readable-core color-depth repair:
    // The mobile idle proof now crops the real crisp core correctly, which exposed
    // a whiteout regression from the readable-core lift. Keep the alpha/size
    // readability repair, but tint the core through cyan depth instead of mixing
    // most of it to white ice.
    vec3 front = mix(vColor, ice, 0.16 + uIdleReadabilityLift * 0.04 + vReadableCore * 0.26 + vRimSpin * 0.13);
    vec3 col = mix(deep, front, vDepth);
    vec3 scanCrest = vec3(0.66, 0.02, 0.05);
    col = mix(col, scanCrest, vScanEnergy * 0.36);
    float depthLight = mix(0.88, 1.32, idleFront);
    float directionalLight = mix(0.92, 1.12, surfaceKey);
    col *= mix(1.0, depthLight * directionalLight, depthCue);
    // WHY 2026-07-30 PF-19 particle-integrated presence:
    // The user wants only a little more glow, but it must reveal the existing
    // multi-scale particle body instead of painting another DOM halo/fog layer.
    // Lift RGB inside the same point sprites, weighted toward readable cores;
    // do not change point count, point size, alpha footprint, or draw-call shape.
    float restParticleGlow =
      (1.0 - clamp(uActiveMotion, 0.0, 1.0)) *
      (0.035 + uIdleReadabilityLift * 0.025 + vReadableCore * 0.045);
    float particleIntegratedPresence =
      uIdleReadabilityLift * 0.035 +
      vReadableCore * 0.08 +
      restParticleGlow;
    col *= vGlow * (
      1.0 +
      uIdleReadabilityLift * 0.08 +
      vReadableCore * 0.48 +
      vRimSpin * 0.16 +
      particleIntegratedPresence
    );

    gl_FragColor = vec4(col, alpha);
    #include <colorspace_fragment>
  }
`;

type FrequencyBands = { bass: number; mid: number; treble: number };
type AudioReactiveFrame = FrequencyBands & { rms: number; peak: number; level: number };
type TimedAudioReactiveFrame = {
  contextTime: number;
  frame: AudioReactiveFrame;
};

const SILENT_AUDIO_REACTIVE_FRAME: AudioReactiveFrame = {
  bass: 0,
  mid: 0,
  treble: 0,
  rms: 0,
  peak: 0,
  level: 0,
};

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothStep01(value: number): number {
  const v = clamp01(value);
  return v * v * (3 - 2 * v);
}

const OPS_ORB_THINKING_PATTERN_SECONDS = 2.4;

export function opsOrbThinkingPatternFrame(elapsedSeconds: number): {
  patternId: number;
  phase: number;
  morph: number;
} {
  const cycle = Math.max(0, elapsedSeconds) / OPS_ORB_THINKING_PATTERN_SECONDS;
  const patternId = Math.floor(cycle);
  const phase = cycle - patternId;
  const assemble = smoothStep01((phase - 0.08) / 0.18);
  const dissolve = 1 - smoothStep01((phase - 0.72) / 0.18);
  return {
    patternId,
    phase,
    morph: clamp01(assemble * dissolve),
  };
}

const ORB_REFERENCE_FRAME_SECONDS = 1 / 60;

// WHY 2026-07-30 PF-17 elapsed-time PCM ownership:
// These coefficients were tuned per 60 Hz frame, so applying them once per RAF
// made the audible PCM envelope release 2-3x slower on 30/20 FPS phones. Convert
// every owned envelope blend/decay from its accepted 60 Hz value to elapsed time;
// phase integration uses the same dt so dropped frames cannot change cadence.
function elapsedFrameCoefficient(
  coefficientAt60Fps: number,
  elapsedSeconds: number,
): number {
  const coefficient = clamp01(coefficientAt60Fps);
  if (coefficient === 0 || coefficient === 1) return coefficient;
  const elapsedFrames = Math.max(0, elapsedSeconds) / ORB_REFERENCE_FRAME_SECONDS;
  return 1 - Math.pow(1 - coefficient, elapsedFrames);
}

function elapsedFrameDecay(
  multiplierAt60Fps: number,
  elapsedSeconds: number,
): number {
  return Math.pow(
    clamp01(multiplierAt60Fps),
    Math.max(0, elapsedSeconds) / ORB_REFERENCE_FRAME_SECONDS,
  );
}

// WHY 2026-06-19 amplitude-channel loudness multiplier:
// Three zones give nonlinear per-syllable response: below threshold reads as
// controlled breathing (×1), medium range gives visible pulse/shake (×1→3),
// and above threshold kicks every channel into a multiplicative burst (×3→12)
// so loud syllables read distinctly louder than quiet ones, not a generic loop.
function computeLoudnessMultiplier(level: number): number {
  if (level < 0.22) return 1;
  if (level < 0.52) return 1 + Math.pow((level - 0.22) / 0.30, 1.18) * 1.85;
  // WHY 2026-06-29 ORB amplitude repair: normal speech should not sit in a
  // permanently huge/glowy state. Keep the lower/mid range controlled and spend
  // the dramatic multiplier only on peak loudness so the Orb visibly jumps,
  // vibrates, and flashes harder when amplitude actually spikes.
  return 2.85 + Math.pow((level - 0.52) / 0.48, 1.88) * 10.4;
}

function getCanvasSize(canvas: HTMLCanvasElement): { width: number; height: number } {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || canvas.clientWidth || 1));
  const height = Math.max(1, Math.round(rect.height || canvas.clientHeight || width));
  return { width, height };
}

function readAnalyserFrame(
  analyser: AnalyserNode | null,
  freqData: Uint8Array<ArrayBuffer>,
  timeData: Uint8Array<ArrayBuffer>,
): AudioReactiveFrame {
  if (!analyser || analyser.context.state !== "running") {
    // WHY 2026-07-29 PF-18 suspended-context boundary:
    // Web Audio may replay the last samples forever while suspended. Explicit
    // zero prevents a frozen syllable from masquerading as live output.
    return SILENT_AUDIO_REACTIVE_FRAME;
  }

  analyser.getByteFrequencyData(freqData);
  analyser.getByteTimeDomainData(timeData);

  let bassSum = 0;
  let midSum = 0;
  let trebleSum = 0;
  let rmsSum = 0;
  let peak = 0;

  for (let i = 0; i < 8; i += 1) bassSum += freqData[i] ?? 0;
  for (let i = 8; i < 24; i += 1) midSum += freqData[i] ?? 0;
  for (let i = 24; i < 48; i += 1) trebleSum += freqData[i] ?? 0;
  for (let i = 0; i < timeData.length; i += 1) {
    const centered = ((timeData[i] ?? 128) - 128) / 128;
    const magnitude = Math.abs(centered);
    rmsSum += centered * centered;
    peak = Math.max(peak, magnitude);
  }
  const rms = Math.sqrt(rmsSum / Math.max(1, timeData.length));

  return {
    bass: bassSum / (8 * 255),
    mid: midSum / (16 * 255),
    treble: trebleSum / (24 * 255),
    rms,
    peak,
    // WHY 2026-06-17 user-reported Orb amplitude bug:
    // Frequency-bin averages missed quiet/loud speech pressure, so the center
    // could keep moving at nearly the same size even when real audio got louder.
    // Time-domain RMS is the actual analyser amplitude; peak keeps consonant hits
    // crisp without replacing RMS as the sustained size/speed source.
    level: clamp01(rms * 3.6 + peak * 0.22),
  };
}

function presentedAudioContextTime(context: BaseAudioContext): number {
  const outputContext = context as BaseAudioContext & {
    getOutputTimestamp?: () => {
      contextTime: number;
      performanceTime: number;
    };
    outputLatency?: number;
    baseLatency?: number;
  };
  try {
    const timestamp = outputContext.getOutputTimestamp?.();
    const contextTime = Number(timestamp?.contextTime);
    const performanceTime = Number(timestamp?.performanceTime);
    const timestampAgeMs = performance.now() - performanceTime;
    if (
      Number.isFinite(contextTime) &&
      contextTime >= 0 &&
      Number.isFinite(performanceTime) &&
      performanceTime > 0 &&
      timestampAgeMs >= -50 &&
      timestampAgeMs <= 250
    ) {
      // WHY 2026-08-02 physical-output clock freshness:
      // Android can briefly return the spec-defined uninitialized 0/0 stamp or
      // repeat a stale hardware stamp. Accepting it froze the Orb behind the
      // audible PCM. Extrapolate only a fresh timestamp to now and clamp it to
      // the graph clock; otherwise use the latency-aware fallback below.
      return Math.min(
        context.currentTime,
        contextTime + Math.max(0, timestampAgeMs) / 1000,
      );
    }
  } catch {
    // Some older WebKit builds expose the method before their output clock is ready.
  }
  const reportedLatency =
    typeof outputContext.outputLatency === "number"
      ? outputContext.outputLatency
      : typeof outputContext.baseLatency === "number"
        ? outputContext.baseLatency
        : 0;
  return Math.max(0, context.currentTime - Math.max(0, reportedLatency));
}

function createSoftPointTexture(size: number): THREE.CanvasTexture {
  const sprite = document.createElement("canvas");
  sprite.width = size;
  sprite.height = size;
  const ctx = sprite.getContext("2d");
  if (!ctx) throw new Error("orb point texture canvas unavailable");

  const center = size / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
  // WHY 2026-06-17/2026-06-20 soft-alpha blend and line-dominance repair:
  // Too little tail reads as on/off transparency, but a needle-bright center
  // also trips the same 1-2px ridge signature as the rejected line network. Keep
  // the sprite visibly detailed, widen the mid falloff, and leave the outer tail
  // sub-visible so the Orb stays a soft particle cloud instead of a flat disk.
  // WHY 2026-06-29 ORB crisp-particle detail repair:
  // After the user asked for lower glow and a smaller amplitude-sensitive Orb,
  // the old broad tail made late speech look like a dim smooth mist and failed
  // the protected particle-detail proof. Keep the same soft-point design, but
  // spend alpha on the core/midpoint and shorten only the faint outer tail.
  // WHY 2026-06-29 mobile active-speaking point-density repair:
  // Stronger center/mid stops give each point a detectable core while the shader
  // cutoff removes the tail. The user sees more separate glints, not a larger
  // blurry Orb, and the line-ball guard still owns rejected lattice regressions.
  gradient.addColorStop(0, "rgba(255,255,255,0.9)");
  gradient.addColorStop(0.07, "rgba(255,255,255,0.72)");
  gradient.addColorStop(0.16, "rgba(255,255,255,0.32)");
  gradient.addColorStop(0.3, "rgba(255,255,255,0.07)");
  gradient.addColorStop(0.46, "rgba(255,255,255,0.01)");
  gradient.addColorStop(0.62, "rgba(255,255,255,0.0015)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(sprite);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createCoreGlowTexture(size: number): THREE.CanvasTexture {
  // WHY 2026-06-20 Orb emitted-core luminosity:
  // The orb canvas had no real emitted light, so it read as a flat wire ball.
  // This core uses a smooth gaussian-like falloff (no crisp center, unlike the
  // particle sprite) so the additive blend reads as soft light emanating from
  // inside the Orb instead of a hard bright dot or a circular plate.
  const sprite = document.createElement("canvas");
  sprite.width = size;
  sprite.height = size;
  const ctx = sprite.getContext("2d");
  if (!ctx) throw new Error("orb core glow texture canvas unavailable");

  const center = size / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
  gradient.addColorStop(0, "rgba(255,255,255,0.82)");
  gradient.addColorStop(0.18, "rgba(255,255,255,0.4)");
  gradient.addColorStop(0.42, "rgba(255,255,255,0.15)");
  gradient.addColorStop(0.68, "rgba(255,255,255,0.04)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(sprite);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

export function createKaiParticleOrb(canvas: HTMLCanvasElement, options: KaiParticleOrbOptions = {}): KaiParticleOrbEngine {
  let destroyed = false;
  let paused = false;
  let reducedMotion = Boolean(options.reducedMotion);
  const animationLease = createOpsOrbAnimationFrameLease({
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    cancelFrame: (frameId) => window.cancelAnimationFrame(frameId),
    renderFrame: animate
  });

  // WHY 2026-06-20 ORB-PF-55 perf repair:
  // The accepted Orb channels are shader-side, but the live Studio also renders
  // the room WebGL canvas and large glow layers. Keep enough particles for a
  // dense glistening body while cutting vertex/fill work so phone-class playback
  // does not feel like it is struggling.
  // WHY 2026-06-21/22 ORB-PF-102/103 live perf proof:
  // The prompt-required life comes from shader breath/wave/vibration/twinkle,
  // not raw vertex count or electron rails. The integrated real Studio proof
  // caught p95=44.7ms when loud speech filled almost the entire canvas, then a
  // real Studio run still starved at p95=79.7ms under load. Proof 45 then showed
  // room-demand rendering brought p95 down to 15.8ms, but the reduced body missed
  // the active-speaking edge/detail floor (0.0115 vs 0.014), then proof 46 with
  // 2300 desktop particles still missed by a sliver at 0.013964 while holding
  // p95=15.4ms. Keep line/electron accents capped and spend that recovered frame
  // budget on real point-cloud detail.
  // WHY 2026-06-30 production mobile p95 repair:
  // The production Windows-Chrome mobile proof kept the accepted point-cloud look
  // but failed frame pacing at p95=100.8ms. Mobile needs a cheaper shader/fill
  // budget than desktop: keep density high enough for crisp glints, but stop
  // spending thousands of translucent sprite fragments and electron accents.
  const particleCount = options.quality === "mobile" ? 3400 : 4000;
  // WHY 2026-08-04 canonical mobile/desktop Orb: quality may reduce only
  // invisible capacity/count and drawing resolution. Geometry, colors, motion,
  // amplitude mapping, point size, glow and accent cadence must remain the same
  // character; those old quality forks caused the phone to alternate between a
  // coarse blob body and the accepted desktop particle body.
  const maxLines = 1180;
  const maxElectrons = 46;

  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    // WHY 2026-06-18 live Orb context-loss repair:
    // The signed-in Studio runs the 3D room WebGL canvas and this particle Orb
    // WebGL canvas at the same time. Desktop MSAA on the small transparent Orb
    // was pushing Chrome into context loss during speech bursts, which made the
    // artist see a dead/flat avatar. Keep the Orb WebGL-only, but use DPR and
    // soft particle textures for edge quality instead of a second MSAA buffer.
    antialias: false,
    canvas,
    depth: false,
    failIfMajorPerformanceCaveat: false,
    powerPreference: "high-performance",
    // WHY 2026-06-17 soft-alpha blend repair:
    // Non-premultiplied transparent pixels can fringe when CSS/filter compositing
    // places the Orb over the bright studio console. Use the browser/WebGL
    // premultiplied-alpha path so half-alpha particle edges blend smoothly with
    // the room instead of snapping between visible and invisible.
    premultipliedAlpha: true,
    // WHY 2026-07-01 KAI-MOTION-TIMING lag-source repair:
    // Preserving the WebGL backbuffer taxes every normal Orb speech frame. Proof
    // captures use a proof-only offscreen render target, so the user path keeps
    // the fast default buffer and the visible runtime canvas is never forced into
    // preserveDrawingBuffer just for tests.
    preserveDrawingBuffer: false,
    stencil: false,
  });

  // WHY 2026-08-04 canonical shader ownership:
  // The former fixed-function fallback was a second, visibly different Orb: it
  // ignored every shader motion uniform and produced the reported big, static
  // blobs. A shader failure must fail closed through the avatar's explicit error
  // owner. It may never replace the accepted body with older behavior.
  let shaderFailed = false;
  renderer.debug.checkShaderErrors = true;
  renderer.debug.onShaderError = () => {
    shaderFailed = true;
  };

  // WHY 2026-07-01 real-Chrome Orb speech p95 repair:
  // The signed-in Studio already drops the background room Canvas to demand/DPR
  // mode during foreground Orb speech, but real Chrome still sampled the Orb RAF
  // at ~88ms while the foreground transparent canvas rendered a full 1x buffer.
  // Keep the CSS/orb composition size and shader-owned point-cloud motion, but
  // cap only the desktop Orb drawing buffer so translucent sprite fill does not
  // dominate the frame budget. Mobile keeps its existing proven pixel ratio path.
  const requestedPixelRatio = options.pixelRatio ?? window.devicePixelRatio ?? 1;
  const initialCanvasSize = getCanvasSize(canvas);
  // The donor's 0.72 DPR cap protects its room-size canvas. At this tiny Ops
  // stage it produces fewer than 60 physical pixels and smears the fine point
  // field. Bound the small surface at 1–2.25 DPR; total fill remains far below
  // the donor desktop budget and phone still changes quality/count only.
  // WHY [BOL-ORB-COMPACT-BOUNDARY-009]: the real 82px mobile root overscans to
  // 160.718px and the 88px desktop root to 172.48px. A <=160 cutoff therefore
  // sent both tiny Ops canvases down the donor room-canvas 0.72-DPR path. Keep
  // the threshold above both exact projections but below a room-size surface.
  // Falsifier: the mounted Ops canvas reports 0.72 DPR or a non-compact canvas
  // inherits the 2.25 cap.
  const compactStage = Math.min(initialCanvasSize.width, initialCanvasSize.height) <= 192;
  const maxPixelRatio = compactStage ? OPS_ORB_COMPACT_MAX_PIXEL_RATIO : 0.72;
  const minPixelRatio = compactStage ? 1 : 0.72;
  const pixelRatio = Math.max(minPixelRatio, Math.min(requestedPixelRatio, maxPixelRatio));
  renderer.setPixelRatio(pixelRatio);
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, 1, 1, 1000);
  camera.position.z = CAMERA_Z;
  const particleTexture = createSoftPointTexture(128);
  const electronTexture = createSoftPointTexture(96);

  const particleGeo = new THREE.BufferGeometry();
  const particlePositions = new Float32Array(particleCount * 3);
  const particlePhase = new Float32Array(particleCount);
  const particleLayer = new Float32Array(particleCount);
  const particleSizeSeed = new Float32Array(particleCount);
  const particleReadableCore = new Float32Array(particleCount);
  const particleConnectionNode = new Float32Array(particleCount);
  const particleBaseColors = new Float32Array(particleCount * 3);
  let readableCoreSeedCount = 0;

  for (let i = 0; i < particleCount; i += 1) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    // WHY 2026-06-20 ORB-PF-03/ORB-MATRIX-10 volumetric body:
    // The old field biased 70% of particles to the shell, so it read as a hollow,
    // see-through decal. Drop to ~50% shell so the interior has real mass and the
    // Orb reads as a luminous 3D object, not line art over a glow.
    const shellParticle = Math.random() < 0.5;
    const radius = shellParticle
      ? 11.5 + Math.pow(Math.random(), 0.34) * 8.5
      : Math.pow(Math.random(), 0.66) * 15.2;
    const i3 = i * 3;
    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.sin(phi) * Math.sin(theta);
    const z = radius * Math.cos(phi);

    particlePositions[i3] = x;
    particlePositions[i3 + 1] = y;
    particlePositions[i3 + 2] = z;
    particlePhase[i] = Math.random() * 1000;
    // aLayer: 0 = deep interior ... 1 = outer shell. Drives interior core-glow
    // and shell wave/breath amplitude in the vertex shader.
    particleLayer[i] = clamp01(radius / 20);
    // WHY 2026-06-17/2026-06-20 Orb hue and color-depth repair:
    // Warm/violet vertex bands made the live Orb feel cheap and disconnected
    // from its cyan glow. The first shader pass then over-corrected into a mostly
    // white/cyan spinner. Keep all detail in the ice/cyan family, but make the
    // rare glints cyan-white rather than literal white and keep most particles
    // in darker linear cyan so the idle body has real depth.
    const yBand = Math.abs(y / Math.max(radius, 0.01));
    const roll = Math.random();
    // WHY 2026-07-01 desktop/live idle readable-core repair:
    // The signed-in Orb proof can run on desktop first; limiting readable cores
    // to mobile left the real desktop Orb as a faint particle cluster that the
    // proof could not isolate and the user could barely see. Give both desktop
    // and mobile a bounded subset of existing points with crisp cores instead:
    // particle count, line/electron caps, DOM glow, whole-body spin, and the
    // rejected contrast branch remain unchanged.
    // WHY 2026-06-30 mobile idle particle-body density repair:
    // Root geometry is now proven fixed, but the crop still has only ~0.3% bright
    // core coverage. Make the real mobile shell carry more readable cores instead
    // of weakening the proof, adding DOM glow, raising line/electron caps, or
    // changing the accepted production speech trough values.
    const readableCoreStrength = shellParticle
      ? roll < 0.46 || (yBand < 0.64 && roll < 0.58)
          ? roll < 0.2
            ? 1.12
            : 0.86
          : 0
      : roll < 0.2
          ? 0.46
          : 0;
    const readableCoreSeed = readableCoreStrength > 0;
    if (readableCoreSeed) readableCoreSeedCount += 1;
    particleReadableCore[i] = readableCoreStrength;
    // Per-particle base size variance feeds independent twinkle.
    particleSizeSeed[i] =
      readableCoreSeed
        ? 1.24 + Math.random() * 0.42
        : 0.6 + Math.random() * 0.85;
    if (shellParticle) {
      // WHY 2026-06-30 production mobile active-detail repair:
      // Prod proof showed the accepted sphere could still dip below the protected
      // edge-detail floor when the sampled syllable had low amplitude. Add a few
      // more cyan-white glint seeds without changing particle count, line caps, or
      // DOM glow, so mobile detail comes from point contrast instead of fill.
      // WHY 2026-06-30 production low-amplitude edge lift:
      // Live production samples still landed just below the protected 0.014
      // active-detail floor while line/electron caps and p95 were already at their
      // safe limits. Spend the repair on sparse point seeds only; the demoted
      // lattice, DOM glow, and particle count remain untouched.
      // WHY 2026-06-30 production low-amplitude glint floor:
      // Clean-Chrome production proof showed the Orb is spherical but quiet
      // speech frames can still sample below the active-detail floor. Raise the
      // sparse cyan-white seed floor only; this preserves particle count,
      // sprite fill, line caps, and the no-whiteout cyan palette.
      const brightCrimson = roll < 0.16 || (yBand < 0.42 && roll < 0.24);
      const electricCrimson = !brightCrimson && roll < 0.78;
      const deepCrimson = !brightCrimson && !electricCrimson && roll < 0.95;
      particleBaseColors[i3] = brightCrimson ? 0.55 : electricCrimson ? 0.42 : deepCrimson ? 0.24 : 0.34;
      particleBaseColors[i3 + 1] = brightCrimson ? 0.008 : electricCrimson ? 0.004 : deepCrimson ? 0.001 : 0.003;
      particleBaseColors[i3 + 2] = brightCrimson ? 0.015 : electricCrimson ? 0.018 : deepCrimson ? 0.008 : 0.014;
    } else if (roll < 0.3) {
      particleBaseColors[i3] = 0.55;
      particleBaseColors[i3 + 1] = 0.008;
      particleBaseColors[i3 + 2] = 0.015;
    } else if (roll < 0.68) {
      particleBaseColors[i3] = 0.66;
      particleBaseColors[i3 + 1] = 0.02;
      particleBaseColors[i3 + 2] = 0.05;
    } else {
      particleBaseColors[i3] = 0.36;
      particleBaseColors[i3 + 1] = 0.004;
      particleBaseColors[i3 + 2] = 0.02;
    }
  }

  particleGeo.setAttribute("position", new THREE.BufferAttribute(particlePositions, 3));
  // `color` is read by BOTH the custom vertex shader (as the linear base palette)
  // inside the canonical ShaderMaterial body.
  particleGeo.setAttribute("color", new THREE.BufferAttribute(particleBaseColors, 3));
  particleGeo.setAttribute("aPhase", new THREE.BufferAttribute(particlePhase, 1));
  particleGeo.setAttribute("aLayer", new THREE.BufferAttribute(particleLayer, 1));
  particleGeo.setAttribute("aSizeSeed", new THREE.BufferAttribute(particleSizeSeed, 1));
  particleGeo.setAttribute("aReadableCore", new THREE.BufferAttribute(particleReadableCore, 1));
  const connectionNodeAttribute = new THREE.BufferAttribute(particleConnectionNode, 1);
  connectionNodeAttribute.setUsage(THREE.DynamicDrawUsage);
  particleGeo.setAttribute("aConnectionNode", connectionNodeAttribute);

  const initialDrawingBufferHeight = Math.max(1, Math.round(getCanvasSize(canvas).height * pixelRatio));

  const particleUniforms = {
    uTime: { value: 0 },
    uBreath: { value: 0.3 },
    uWave: { value: 0.15 },
    uWaveDir: { value: new THREE.Vector3(0, 1, 0) },
    uTwinkle: { value: 0.25 },
    uVibration: { value: 0 },
    uVibRate: { value: 1 },
    uPulse: { value: 0 },
    uPulsePhase: { value: 0 },
    uJumpScale: { value: 0 },
    uRadiusScale: { value: 1 },
		    uCoreGlow: { value: 0 },
		    uMicroTwinkle: { value: 0.04 },
		    uSparkleEnergy: { value: 0 },
		    uMicroVariance: { value: 0.04 },
		    uIdleReadabilityLift: { value: 0 },
		    uIdleDepthCue: { value: 0 },
		    uRimSpin: { value: 0 },
		    uActiveMotion: { value: 0 },
		    uScanBlend: { value: 0 },
		    uScanTravel: { value: reducedMotion ? 0.22 : 1 },
		    uScanScale: { value: OPS_ORB_SCAN_RADIUS_SCALE },
		    uConnectionMorph: { value: 0 },
		    uSize: { value: 0.9 },
    uPointDiameterScale: { value: OPS_ORB_POINT_DIAMETER_SCALE },
    uBasePointFloorCssPx: { value: OPS_ORB_BASE_POINT_FLOOR_CSS_PX },
    uReadablePointFloorCssPx: { value: OPS_ORB_READABLE_POINT_FLOOR_CSS_PX },
    uScale: { value: initialDrawingBufferHeight * 0.5 },
    uPixelRatio: { value: pixelRatio },
    uCameraZ: { value: CAMERA_Z },
    uDepthRange: { value: 30 },
    uOpacity: { value: 0.78 },
    uMap: { value: particleTexture },
  } satisfies Record<string, THREE.IUniform>;

  const shaderMat = new THREE.ShaderMaterial({
    uniforms: particleUniforms,
    vertexShader: ORB_VERTEX_SHADER,
    fragmentShader: ORB_FRAGMENT_SHADER,
    // Match the prior body: alpha-blended ice/cyan particles over the transparent
    // room (NOT additive, which white-outs across thousands of soft sprites).
    blending: THREE.NormalBlending,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  });

  const particles = new THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>(particleGeo, shaderMat);
  scene.add(particles);

  const linePositions = new Float32Array(maxLines * 6);
  const lineColors = new Float32Array(maxLines * 6);
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute("position", new THREE.BufferAttribute(linePositions, 3));
  lineGeo.setAttribute("color", new THREE.BufferAttribute(lineColors, 3));
  lineGeo.setDrawRange(0, 0);

  const lineMat = new THREE.LineBasicMaterial({
    // WHY 2026-06-20 ORB-PF-10/12 demoted line accent:
    // The proximity lattice used to be the dominant high-contrast read ("lines on
    // a ball"). It is now a faint, blended accent well below the particle body —
    // few lines, low opacity — so the GPU particle field is the body and lines
    // only hint structure. Vertex-colored to blend into the ice/cyan palette.
    blending: THREE.NormalBlending,
    color: 0xffffff,
    depthWrite: false,
    opacity: 0,
    transparent: true,
    vertexColors: true,
  });

  const lines = new THREE.LineSegments(lineGeo, lineMat);
  scene.add(lines);

  const electronGeo = new THREE.BufferGeometry();
  const electronPositions = new Float32Array(maxElectrons * 3);
  electronGeo.setAttribute("position", new THREE.BufferAttribute(electronPositions, 3));
  electronGeo.setDrawRange(0, 0);

  const electronMat = new THREE.PointsMaterial({
    blending: THREE.AdditiveBlending,
    color: 0xff6a5f,
    depthWrite: false,
    map: electronTexture,
    opacity: 1,
    size: 0.8,
    sizeAttenuation: true,
    transparent: true,
  });

  const electronPoints = new THREE.Points(electronGeo, electronMat);
  scene.add(electronPoints);

  // WHY 2026-06-20 Orb emitted-core luminosity + line-glow blend:
  // `core` is a single camera-facing additive Sprite — the one in-canvas light
  // source ("light from within", ORB-PF-28/36) — sized in world units (a Sprite,
  // never clipped by the mobile gl_PointSize cap). `lineGlow` shares the SAME
  // lineGeo as the demoted accent (zero extra connection cost) but blends
  // additively, so it only blooms the faint accent on loud syllables. No extra
  // render pass: UnrealBloomPass was rejected because it replaces renderer.render
  // with a multi-pass composer that fights the transparent clear
  // (setClearColor 0x000000,0), premultiplied alpha, one-shot proof readback,
  // and the dual-canvas context-loss budget (antialias:false).
  const coreTexture = createCoreGlowTexture(256);
  const coreMat = new THREE.SpriteMaterial({
    blending: THREE.AdditiveBlending,
    color: 0xff7368,
    depthTest: false,
    depthWrite: false,
    map: coreTexture,
    opacity: 0,
    transparent: true,
  });
  const core = new THREE.Sprite(coreMat);
  core.renderOrder = 2;
  core.scale.set(14, 14, 1);
  scene.add(core);

  const lineGlowMat = new THREE.LineBasicMaterial({
    blending: THREE.AdditiveBlending,
    color: 0xff938a,
    depthWrite: false,
    opacity: 0,
    transparent: true,
    vertexColors: true,
  });
  const lineGlow = new THREE.LineSegments(lineGeo, lineGlowMat);
  scene.add(lineGlow);

  let state: KaiParticleOrbState = "idle";
  let lastState: KaiParticleOrbState = "idle";
  let scanBlend = 0;
  // WHY 2026-06-29 ORB idle-radius proof repair:
  // The legacy 22px startup value made the post-speech "idle" baseline stay
  // large for several seconds because idle intentionally settles slowly. Start
  // at the actual idle target so rest is small and speech owns the swell.
  let targetRadius = 14.6;
  let currentRadius = targetRadius;
  let targetSpeed = 0.3;
  let currentSpeed = 0.3;
  let targetSpeechSpinMultiplier = 1;
  let speechSpinMultiplier = 1;
  let targetBright = 0.34;
  let currentBright = 0.34;
  let targetSize = 0.38;
  let currentSize = 0.38;
  let lineAmount = 0;
  let visibleLineCount = 0;
  let visibleElectronCount = 0;
  let frameCount = 0;
  let lastConnectionBuildFrame = -999;
  let lastConnectionLineAmount = 0;
  let activeThinkingPatternId = -1;
  let thinkingConnectionMorph = 0;
  let thinkingConnectionPatternReady = false;
  let targetLineAmount = 0;
  let spinX = 0;
  let spinY = 0;
  let spinZ = 0;
  let transitionEnergy = 0;
  let cloudZ = 0;
  let cloudZVelocity = 0;
  let vortexStrength = 0;
  let targetVortex = 0;
  let breathAmp = 0;
  let targetBreathAmp = 0;
  let shockwave = 0;
  let previousBass = 0;
  let electronSpawnRate = 0;
  let targetElectronRate = 0;
  let lastElectronSpawn = 0;
  let analyser: AnalyserNode | null = null;
  let analyserTiming: KaiOrbAnalyserTiming = "output";
  let pcmSpeechLeaseActive = false;
  let freqData: Uint8Array<ArrayBuffer> = new Uint8Array(64);
  let timeData: Uint8Array<ArrayBuffer> = new Uint8Array(128);
  let speechLevel = 0;
  let audioAmplitudeLevel = 0;
  let renderedOutputLevel = 0;
  let speechAmplitudeLevel = 0;
  let pcmPeakExpansionFactor = 1;
  let pcmSpectralAccent = 1;
  let pendingElectronBurst = 0;
  let ambientIdleTimer: number | null = null;
  let ambientIdleStep = 0;
  // WHY 2026-06-19 amplitude channel state:
  // Each channel has its own smoothed value so they can attack/decay at
  // independent rates — loudnessMultiplier leads, others follow from it.
  let loudnessMultiplier = 1;
  let pulseScale = 0;
  // WHY 2026-06-21 ORB-PF-104 smooth glow ramp: a slow loudness envelope that the
  // core glow follows so brightness curves on/off instead of strobing per syllable.
  let glowEnvelope = 0;
  let pulsePhase = 0;
  let vibrationLevel = 0;
  // WHY 2026-06-20 ORB-PF-37/38:
  // Vibration is not a constant wobble and not faster spin. This rate drives
  // speaking-only particle/body buzz faster as amplitude rises, while the
  // shader's small per-particle travel keeps it a tight buzz, not a broad slide.
  let vibrationRate = 1;
  let shakeLevel = 0;
  let flareLevel = 0;
  // WHY 2026-06-20 ORB-PF-60 vibration-not-shake:
  // `bodyJitterAmount` is preserved as a callback/dataset channel, but it now
  // drives the PER-PARTICLE vibration uniform (uVibration) in the vertex shader,
  // not a whole-object .position offset. Moving the whole mass read as a broad
  // shake; per-particle high-frequency displacement reads as a tight buzz.
	  let bodyJitterAmount = 0;
	  let coreGlowLevel = 0;
	  let lineGlowLevel = 0;
	  let particleBreathLevel = 0;
	  // WHY 2026-06-21 ORB-PF-90/92:
	  // The GPU rewrite kept performance but removed the old named micro-particle
	  // lanes, which let future fixes pass by changing only spin/radius/glow. These
	  // explicit channels protect the breathing/glistening "alive" layer as its own
	  // amplitude-driven contract without restoring the phone-heavy CPU loop.
	  let microTwinkleLevel = 0.04;
	  let sparkleEnergy = 0;
	  let microVarianceLevel = 0.04;
	  let lastAnimateTime = -1;
  const analyserFrameHistory: TimedAudioReactiveFrame[] = [];
  let presentedAnalyserFrame = SILENT_AUDIO_REACTIVE_FRAME;
  let lastPresentedContextTime = 0;
  const activeConnections: Connection[] = [];
  const connectionPool: Connection[] = Array.from({ length: 500 }, () => ({
    x1: 0,
    y1: 0,
    z1: 0,
    x2: 0,
    y2: 0,
    z2: 0,
  }));
  const activeElectrons: Electron[] = [];

  const clock = new THREE.Clock();

  const coreSpeechColor = new THREE.Color(0xb5091e);
  const brightColor = new THREE.Color(0xffded8);
  const tmpWaveDir = new THREE.Vector3();

  function clearAnalyserFrameHistory(): void {
    analyserFrameHistory.length = 0;
    presentedAnalyserFrame = SILENT_AUDIO_REACTIVE_FRAME;
    lastPresentedContextTime = 0;
  }

  function readOutputAlignedAnalyserFrame(): AudioReactiveFrame {
    if (!analyser || analyser.context.state !== "running") {
      clearAnalyserFrameHistory();
      return SILENT_AUDIO_REACTIVE_FRAME;
    }

    const rawFrame = readAnalyserFrame(analyser, freqData, timeData);
    if (analyserTiming === "input") {
      // Microphone capture is already the presented physical signal. Reusing
      // the speaker-output latency ledger here made the listening Orb lag the
      // owner's voice; only scheduled Luna output needs clock alignment.
      clearAnalyserFrameHistory();
      return rawFrame;
    }
    const renderedContextTime = analyser.context.currentTime;
    const previous = analyserFrameHistory.at(-1);
    if (previous && Math.abs(previous.contextTime - renderedContextTime) < 0.0001) {
      previous.frame = rawFrame;
    } else {
      analyserFrameHistory.push({
        contextTime: renderedContextTime,
        frame: rawFrame,
      });
    }

    const presentedContextTime = presentedAudioContextTime(analyser.context);
    if (presentedContextTime + 0.5 < lastPresentedContextTime) {
      clearAnalyserFrameHistory();
      return SILENT_AUDIO_REACTIVE_FRAME;
    }
    lastPresentedContextTime = presentedContextTime;

    // WHY 2026-07-31 physical-output clock ownership:
    // AnalyserNode exposes the graph's rendered samples, which can lead the
    // phone speaker by the device output buffer. Tag those frames with context
    // time and release only frames the output timestamp says have reached the
    // device. Audio itself is never delayed; the Orb follows within one display
    // frame and cannot jump before a scheduled syllable is audible.
    while (
      analyserFrameHistory.length > 0 &&
      (analyserFrameHistory[0]?.contextTime ?? Number.POSITIVE_INFINITY) <=
        presentedContextTime + 0.0001
    ) {
      presentedAnalyserFrame =
        analyserFrameHistory.shift()?.frame ?? presentedAnalyserFrame;
    }
    if (analyserFrameHistory.length > 240) {
      analyserFrameHistory.splice(0, analyserFrameHistory.length - 240);
    }
    return presentedAnalyserFrame;
  }

  function hasPcmSpeechAuthority(): boolean {
    return (
      analyserTiming === "output" &&
      (analyser != null || pcmSpeechLeaseActive)
    );
  }

  function releasePcmSpeechLeaseIfSettled(): void {
    if (
      analyser == null &&
      state !== "speaking"
    ) {
      pcmSpeechLeaseActive = false;
    }
  }

  function isSpeechVisualActive(): boolean {
    // This level can rise only from the output analyser. After that analyser is
    // removed it can only decay, providing the bounded visual release without a
    // transcript, state, timer, microphone, or second motion owner.
    return renderedOutputLevel > 0.002;
  }

  function isAudiblePcmActive(): boolean {
    return analyserTiming === "output" && analyser != null && audioAmplitudeLevel > 0.002;
  }

  function shouldRunAmbientIdleHeartbeat(): boolean {
    return (
      !hasPcmSpeechAuthority() &&
      !isSpeechVisualActive() &&
      (state === "idle" || state === "listening")
    );
  }

  function resize(): void {
    const { width, height } = getCanvasSize(canvas);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
    // Keep the point-size attenuation scale tied to the real drawing buffer
    // height so particles stay the same on-screen size across DPR/resize.
    particleUniforms.uScale.value = Math.max(1, height * pixelRatio) * 0.5;
  }

  function setTargets(): void {
    targetSpeechSpinMultiplier = 1;

    if (state === "listening" && !isSpeechVisualActive()) {
      targetRadius = 12.5;
      targetSpeed = 0.24;
      targetBright = 0.55;
      targetSize = 0.68;
      targetLineAmount = 0.18;
      targetElectronRate = 0.003;
      targetVortex = 0.16;
      targetBreathAmp = 0.1;
    } else if (state === "thinking" && !isSpeechVisualActive()) {
      targetRadius = 9;
      targetSpeed = 0.46;
      targetBright = 0.82;
      targetSize = 0.64;
      targetLineAmount = 0.4;
      targetElectronRate = 0.015;
      targetVortex = 0.72;
      targetBreathAmp = 0.35;
    } else if (isSpeechVisualActive()) {
      // WHY 2026-06-17 amplitude-locked Orb repair:
      // Speech maps analyser/boundary amplitude into the actual particle body:
      // quiet speech stays controlled, loud speech opens freely and speeds up in
      // the same frame.
      const speechPressure = hasPcmSpeechAuthority()
        ? opsOrbAudioEnvelope(renderedOutputLevel)
        : Math.pow(clamp01(speechLevel), 1.7);
      // WHY 2026-06-29 late-speech particle floor:
      // Proof caught valid low-level speech frames collapsing into a tiny particle
      // core while the protected blurred halo stayed wide. Keep amplitude variation
      // in speed/spin/shock, but give the visible particle body a speech-only floor
      // so quiet sustained words still read as a round, detailed Orb.
      // WHY 2026-06-30 production mobile low-amplitude sphere repair:
      // The foreground glow clamp produced enough high-frequency detail, but a
      // quiet speech trough collapsed into a sparse horizontal strip. Keep the
      // amplitude-driven growth above this floor, and lift only the shader-owned
      // particle body so trough frames stay round without adding DOM wash.
      const speechVisualPressure = speechPressure;
      // WHY 2026-06-18 high-amplitude spin: quiet speech stays controlled, upper
      // amplitude ramps nonlinearly. Spin is one channel and is demoted at apply
      // time so it never becomes the whole speaking animation (ORB-PF-11/12).
      const highAmplitudeSpinMultiplier = Math.pow(smoothStep01((speechPressure - 0.58) / 0.42), 2.35);
      const syllableSpinKick = Math.pow(clamp01(shockwave), 0.74) * 0.86;
      // WHY 2026-06-29 mobile amplitude sensitivity repair:
      // The previous 19.2px speaking floor kept low/normal speech too close to
      // loud speech, so the Orb looked like one big constant state. Drop the
      // speech floor back near idle and move the size/speed growth into nonlinear
      // amplitude and shockwave terms: quiet speech stays small, loud syllables
      // visibly swell/vibrate, and spin remains a late peak-only secondary cue.
      targetRadius = 14.8 + speechVisualPressure * 18.6 + highAmplitudeSpinMultiplier * 10.5 + syllableSpinKick * 5.6;
      targetSpeechSpinMultiplier = 1 + highAmplitudeSpinMultiplier * 2.8 + syllableSpinKick * 0.36;
      targetSpeed = 0.035 + speechPressure * 1.35 + highAmplitudeSpinMultiplier * 8.4 + syllableSpinKick * 3.6;
      targetBright = 0.46 + speechVisualPressure * 0.3 + highAmplitudeSpinMultiplier * 0.22;
      targetSize = 0.5 + speechVisualPressure * 0.34 + highAmplitudeSpinMultiplier * 0.11;
      // WHY 2026-06-20 ORB-PF-10 demoted line accent: keep the speech line target
      // low so the GPU particle body and in/out wave are the primary read and the
      // faint accent never returns to "cheap white lines".
      targetLineAmount = 0.08 + speechPressure * 0.13;
      targetElectronRate = 0.003 + speechPressure * 0.07 + highAmplitudeSpinMultiplier * 0.17;
      targetVortex = 0.03 + speechPressure * 0.86 + highAmplitudeSpinMultiplier * 3.45;
      targetBreathAmp = 0.18 + speechVisualPressure * 3.05 + highAmplitudeSpinMultiplier * 6.35 + syllableSpinKick * 4.2;
    } else {
      targetRadius = 14.6;
      targetSpeed = 0.006;
      // WHY 2026-06-29 ORB idle-small repair: rest must be a small, mostly still
      // living particle body. Earlier readability floors kept the Orb nearly as
      // large as speech, making amplitude growth look flat on mobile. Keep only
      // enough brightness/size for subtle breathing particles; speech owns the
      // large nonlinear radius/wave/glow state.
      // WHY 2026-06-30 mobile coherent-sphere density repair:
      // The p95-safe mobile repair made idle mechanically valid but visually read
      // as a tiny sparse glitter cluster in the real signed-in phone frame. Lift
      // only the shader particle body baseline; speech still owns large motion,
      // lines/electrons stay demoted, and DOM glow remains out of this repair.
      targetBright = 0.46;
      targetSize = 0.54;
      targetLineAmount = 0.08;
      targetElectronRate = 0;
      targetVortex = 0.02;
      targetBreathAmp = 0.004;
    }

  }

  // WHY 2026-06-20 ORB-PF-62 demoted proximity-line accent:
  // The connection rebuild is kept ONLY as a faint structural accent and to feed
  // the electron rails. It now connects rest positions (the shader animates the
  // body), steps far more coarsely, and caps to a small line count, so the old
  // O(n^2) per-frame cost that janked on phone (ORB-PF-55) is cut by ~10-50x and
  // the lattice can never dominate the particle body again.
  function clearThinkingPatternNodes(): void {
    particleConnectionNode.fill(0);
    connectionNodeAttribute.needsUpdate = true;
    activeThinkingPatternId = -1;
    thinkingConnectionMorph = 0;
    thinkingConnectionPatternReady = false;
    particleUniforms.uConnectionMorph.value = 0;
  }

  function updateConnections(bass: number, shock: number, t: number): void {
    const speaking = isSpeechVisualActive();
    const thinkingConnectionField = state === "thinking" && !speaking;
    const patternFrame = thinkingConnectionField
      ? opsOrbThinkingPatternFrame(t)
      : null;
    const patternChanged =
      patternFrame != null && patternFrame.patternId !== activeThinkingPatternId;

    if (!thinkingConnectionField && activeThinkingPatternId >= 0) {
      clearThinkingPatternNodes();
    } else if (patternChanged && patternFrame != null) {
      // WHY [BOL-ORB-THINKING-MORPH-015]: never rewrite visible topology. Open
      // the cloud, clear the previous rails/electrons, install the next stable
      // node set, then let the shared GPU morph assemble it. Falsifier: lines
      // jump between patterns, dots do not meet endpoints, or stale electrons
      // cross the dissolve boundary.
      particleConnectionNode.fill(0);
      connectionNodeAttribute.needsUpdate = true;
      activeThinkingPatternId = patternFrame.patternId;
      thinkingConnectionPatternReady = false;
      activeConnections.length = 0;
      activeElectrons.length = 0;
      electronGeo.setDrawRange(0, 0);
    }

    thinkingConnectionMorph =
      patternFrame != null && thinkingConnectionPatternReady
        ? patternFrame.morph * scanBlend
        : 0;
    particleUniforms.uConnectionMorph.value = thinkingConnectionMorph;

    if (lineAmount <= 0.01) {
      lineGeo.setDrawRange(0, 0);
      activeConnections.length = 0;
      visibleLineCount = 0;
      return;
    }

    const particlePosition = particleGeo.getAttribute("position") as THREE.BufferAttribute;
    const particleArray = particlePosition.array as Float32Array;
    const lineColorSource = particleGeo.getAttribute("color") as THREE.BufferAttribute;
    const particleColors = lineColorSource.array as Float32Array;
    const linePosition = lineGeo.getAttribute("position") as THREE.BufferAttribute;
    const lineArray = linePosition.array as Float32Array;
    const lineColor = lineGeo.getAttribute("color") as THREE.BufferAttribute;
    const lineColorArray = lineColor.array as Float32Array;
    const maxDistance = LINE_DISTANCE * (
      (thinkingConnectionField ? 1.34 : 1)
        + bass * (speaking ? 0.6 : 0.3)
        + shock * 0.2
    );
    const maxDistanceSq = maxDistance * maxDistance;
    // Hard cap: the accent must stay faint and few. Far below the old 3000+
    // floor, and low enough that periodic rebuilds cannot dominate frame p95.
    // WHY 2026-06-30 production mobile micro-arc detail repair:
    // The low-amplitude sphere floor fixed shape, but the proof still needed a
    // few more high-frequency edges. Keep mobile below the proof's line-network
    // count gate (30) and lift only the capped accent, not the particle fill.
    // WHY 2026-06-30 mobile edge-detail close-gap repair:
    // The halo cap left spherical speech frames just below the active-detail
    // floor while line dominance stayed far below the rejected lattice cap. Let
    // mobile reach the proof's line-network count gate, but never cross it.
    const accentLineCap = thinkingConnectionField ? 64 : 24;
    const scanLinkPulse = 0.5 + 0.5 * Math.sin(t * 3.73);
    const lineOpacity = thinkingConnectionField
      ? Math.min(0.072, 0.024 + lineAmount * 0.064 + scanBlend * scanLinkPulse * 0.018) *
        thinkingConnectionMorph
      : Math.min(0.006, lineAmount * 0.0062 + shock * 0.002);
    // WHY 2026-06-20/2026-06-21 ORB-PF-55 connection-throttle:
    // Connections use rest positions while the shader animates the particle
    // body, so rebuilding them every RAF burns CPU without adding visible life.
    // Reuse the last faint accent for a few frames; the breathing/vibration/wave
    // channels still update every frame on the GPU. Keep this cache long enough
    // to avoid line-search bursts in the speech p95 budget; electrons can ride
    // the retained rails because they are only a sparkle accent.
    const canReuseConnections =
      thinkingConnectionField
        ? thinkingConnectionPatternReady && !patternChanged && visibleLineCount > 0
        : visibleLineCount > 0 &&
          frameCount - lastConnectionBuildFrame < 14 &&
          Math.abs(lineAmount - lastConnectionLineAmount) < 0.08;
    if (canReuseConnections) {
      lineMat.opacity = lineOpacity;
      return;
    }
    lastConnectionBuildFrame = frameCount;
    lastConnectionLineAmount = lineAmount;
    // Coarse stride: sample ~42 source particles, not the whole field. The GPU
    // point cloud carries visual richness; this CPU pass only feeds faint rails.
    const step = Math.max(22, Math.floor(particleCount / (thinkingConnectionField ? 68 : 42)));
    const sampleOffset = thinkingConnectionField
      ? ((patternFrame?.patternId ?? 0) * 17) % step
      : 0;
    let lineCount = 0;

    if (thinkingConnectionField) particleConnectionNode.fill(0);

    for (let i = sampleOffset; i < particleCount && lineCount < accentLineCap; i += step) {
      const i3 = i * 3;
      const x1 = particleArray[i3]!;
      const y1 = particleArray[i3 + 1]!;
      const z1 = particleArray[i3 + 2]!;

      for (let j = i + step; j < particleCount && lineCount < accentLineCap; j += step) {
        const j3 = j * 3;
        const dx = particleArray[j3]! - x1;
        const dy = particleArray[j3 + 1]! - y1;
        const dz = particleArray[j3 + 2]! - z1;

        if (dx * dx + dy * dy + dz * dz < maxDistanceSq) {
          const idx = lineCount * 6;
          lineArray[idx] = x1;
          lineArray[idx + 1] = y1;
          lineArray[idx + 2] = z1;
          lineArray[idx + 3] = particleArray[j3]!;
          lineArray[idx + 4] = particleArray[j3 + 1]!;
          lineArray[idx + 5] = particleArray[j3 + 2]!;
          lineColorArray[idx] = particleColors[i3]!;
          lineColorArray[idx + 1] = particleColors[i3 + 1]!;
          lineColorArray[idx + 2] = particleColors[i3 + 2]!;
          lineColorArray[idx + 3] = particleColors[j3]!;
          lineColorArray[idx + 4] = particleColors[j3 + 1]!;
          lineColorArray[idx + 5] = particleColors[j3 + 2]!;
          if (thinkingConnectionField) {
            particleConnectionNode[i] = 1;
            particleConnectionNode[j] = 1;
          }
          lineCount += 1;
        }
      }
    }

    lineGeo.setDrawRange(0, lineCount * 2);
    linePosition.needsUpdate = true;
    lineColor.needsUpdate = true;
    // WHY 2026-06-20 ORB-PF-67 line-dominance proof repair:
    // After core glow was correctly amplitude-gated, the proximity accent became
    // a higher share of bright pixels. Keep this line pass as a barely-there depth
    // hint and let the shader points carry the readable body.
    lineMat.opacity = lineOpacity;
    visibleLineCount = lineCount;

    if (thinkingConnectionField) {
      thinkingConnectionPatternReady = true;
      thinkingConnectionMorph = (patternFrame?.morph ?? 0) * scanBlend;
      particleUniforms.uConnectionMorph.value = thinkingConnectionMorph;
      connectionNodeAttribute.needsUpdate = true;
    }

    const connectionLimit = Math.min(lineCount, 500);
    activeConnections.length = 0;
    for (let c = 0; c < connectionLimit; c += 1) {
      const ci = c * 6;
      const connection = connectionPool[c]!;
      connection.x1 = lineArray[ci]!;
      connection.y1 = lineArray[ci + 1]!;
      connection.z1 = lineArray[ci + 2]!;
      connection.x2 = lineArray[ci + 3]!;
      connection.y2 = lineArray[ci + 4]!;
      connection.z2 = lineArray[ci + 5]!;
      activeConnections.push(connection);
    }
  }

  function updateElectrons(t: number, shock: number, frameScale: number): void {
    const speaking = isSpeechVisualActive();
    const thinking = state === "thinking";
    if (thinking && thinkingConnectionMorph < 0.08 && activeElectrons.length > 0) {
      activeElectrons.length = 0;
      electronGeo.setDrawRange(0, 0);
    }
    // WHY 2026-06-20 ORB-PF-62 capped electron accent: electrons ride the demoted
    // line accent and are a sparkle layer, not a primary read, so the active caps
    // and floors are lower than the old lattice-era values.
    const maxActiveElectrons = speaking
      ? maxElectrons
      : thinking && thinkingConnectionMorph > 0.18
        ? 16
        : 0;
    const speechElectronFloor = speaking ? 20 : 0;
    const spawnGap = speaking ? 0.032 : thinking ? 0.11 : 1.2;
    const speed = speaking
      ? (0.016 + Math.random() * 0.016) * (1 + speechLevel * 0.85)
      : thinking
        ? 0.012 + Math.random() * 0.01
        : 0.0015;

    const spawnElectron = (electronSpeed: number): void => {
      const connection = activeConnections[Math.floor(Math.random() * activeConnections.length)];
      if (!connection) return;
      activeElectrons.push({
        sx: connection.x1,
        sy: connection.y1,
        sz: connection.z1,
        ex: connection.x2,
        ey: connection.y2,
        ez: connection.z2,
        t: 0,
        speed: electronSpeed,
      });
    };

    while (
      pendingElectronBurst > 0 &&
      activeConnections.length > 0 &&
      activeElectrons.length < maxActiveElectrons
    ) {
      spawnElectron(speed * 1.35);
      pendingElectronBurst -= 1;
    }

    if (activeConnections.length > 0 && electronSpawnRate > 0.005 && activeElectrons.length < maxActiveElectrons && t - lastElectronSpawn > spawnGap) {
      spawnElectron(speed);
      lastElectronSpawn = t;
    }

    if (activeConnections.length > 0 && speechElectronFloor > 0) {
      let speechFillBudget = Math.min(6, Math.ceil(1 + speechLevel * 3 + shock * 4));
      while (
        speechFillBudget > 0 &&
        activeElectrons.length < Math.min(maxActiveElectrons, speechElectronFloor + Math.ceil(shock * 8))
      ) {
        spawnElectron(speed * (1.14 + Math.random() * 0.55));
        speechFillBudget -= 1;
      }
    }

    const electronPosition = electronGeo.getAttribute("position") as THREE.BufferAttribute;
    const electronArray = electronPosition.array as Float32Array;
    let aliveCount = 0;

    for (let e = activeElectrons.length - 1; e >= 0; e -= 1) {
      const electron = activeElectrons[e]!;
      electron.t += electron.speed * frameScale;
      if (electron.t >= 1) {
        activeElectrons.splice(e, 1);
        continue;
      }

      const index = aliveCount * 3;
      electronArray[index] = electron.sx + (electron.ex - electron.sx) * electron.t;
      electronArray[index + 1] = electron.sy + (electron.ey - electron.sy) * electron.t;
      electronArray[index + 2] = electron.sz + (electron.ez - electron.sz) * electron.t;
      aliveCount += 1;
    }

    electronGeo.setDrawRange(0, aliveCount);
    visibleElectronCount = aliveCount;
    electronPosition.needsUpdate = true;
    electronPoints.rotation.set(spinX, spinY, spinZ);
    electronPoints.position.z = cloudZ;
    electronMat.size = speaking ? 1.12 + shock * 0.5 : 0.68;
    electronMat.opacity = speaking
      ? 0.88 + shock * 0.1
      : 0.9 * thinkingConnectionMorph;
  }

  // WHY 2026-06-20 ORB-PF-62 channels -> shader uniforms:
  // The smoothed channel scalars (already computed by the proven state machine)
  // are mapped here into the per-frame shader uniforms. This is the single place
  // the body motion is driven, so the off-rAF speech trail and the idle heartbeat
  // can render identical motion just by advancing uTime and re-calling this.
  function setBodyUniforms(t: number): void {
    const speaking = isSpeechVisualActive();
    particleUniforms.uTime.value = t;
    const pcmAmplitudeDrive = isSpeechVisualActive()
      ? opsOrbAudioEnvelope(renderedOutputLevel)
      : null;
    // The calibrated convex envelope already spends its range on true Aoede
    // crests. A second peak multiplier would distort that one audio authority.
    pcmPeakExpansionFactor = 1;
    const pcmPeakExtentDrive =
      pcmAmplitudeDrive == null
        ? null
        : pcmAmplitudeDrive;

    // WHY 2026-06-21 ORB-PF-103/105 exponential, non-uniform speech swell:
    // Growth must ACCELERATE with loudness (a quiet syllable barely grows, a loud
    // one swells dramatically — the user's "pulsing to the loudness"), and it must
    // read as a NON-UNIFORM wave/wiggle, not a clean uniform scale. `speechDrive`
    // is a convex (pow>1) loudness term; `loudnessBoost` adds the upper-amplitude
    // exponential zone. They feed the per-particle wave/breath displacement AND a
    // convex radius/jump scale below. No shader radius clamp: the user explicitly
    // wants the Orb free to expand instead of being cropped/contained.
    const speechDrive = speaking ? Math.pow(clamp01(speechLevel), 1.6) : 0;
    const loudnessBoost = speaking ? clamp01((loudnessMultiplier - 1) / 11) : 0;
    // WHY 2026-08-04 one visual profile: viewport quality must not clamp or
    // boost speech geometry, glints, glow or particle fill. The accepted desktop
    // profile is canonical; mobile may reduce only particle count/drawing cost.
    const bodyBreathForUniforms = particleBreathLevel;
    const microTwinkleForUniforms = microTwinkleLevel;
    const sparkleEnergyForUniforms = sparkleEnergy;
    const microVarianceForUniforms = microVarianceLevel;
    const visualAmplitudeDrive = pcmAmplitudeDrive ?? speechDrive;
    const speechTwinkleForUniforms = speaking
      ? 0.38 + visualAmplitudeDrive * 2.35
      : 0.22;
	    const twinkleForUniforms = speechTwinkleForUniforms;
    // WHY 2026-07-02 speaking-readable-core proof repair:
    // The readable-core lift started as an idle-only proof fix, but the real body
    // crop then showed idle was dense while active speech became sparse. Keep the
    // same bounded core seeds during speech too, with a smaller speech-safe lift,
    // so speech motion rides on wave/vibration/shock instead of stripping the
    // body that the proof and user both need to see.
    const readableCoreLift = speaking
      ? 0.34 + visualAmplitudeDrive * 0.18
      : 0.48;
    const idleReadableCoreActive = !speaking && readableCoreLift > 0;
    const particleDepthCue = speaking
      ? 0.82 + visualAmplitudeDrive * 0.14
      : shouldRunAmbientIdleHeartbeat()
        ? 1
        : 0.72;
    // WHY 2026-07-30 PF-19 state ownership: active speech and thinking retain
    // their accepted GPU articulation. Idle/listening own exactly one
    // time-varying channel—the radial breath—so zero PCM cannot compete with
    // any speech-like rim, wave or micro cadence.
    const activeFineMotion = speaking || state === "thinking";
    const activeMotionWeight =
      pcmAmplitudeDrive == null
        ? activeFineMotion
          ? 1
          : 0
        : smoothStep01(pcmAmplitudeDrive / 0.055);
    // WHY 2026-07-30 PF-19 visible idle presence:
    // The old mounted desktop body was only 90px against a 510px PCM peak. Keep
    // one bounded rest envelope, driven by the exact same slow phase as the
    // vertex breath, so idle/listening stay clearly present without borrowing
    // speech pulses, camera motion, point-size growth, or detached glow.
    const stablePresenceScale = OPS_ORB_IDLE_RADIUS_SCALE;
    const idlePresenceBreath =
      (1 - activeMotionWeight) * Math.sin(t * 1.35) * 0.07;
    // WHY 2026-08-04 one canonical body: production never rotates the shell.
    // Speech reads through output PCM radius/glow/ripple, not an alternate mode.
    const borderSpinForUniforms = 0;

    // Breath present at idle (alive) and swells on speech: a slow radial in/out kept
    // as a subtle "wave breathing" floor at idle (ORB-PF-103), swelling convexly while
    // speaking so the body waves bigger/smaller (non-uniform, not a flat uniform scale).
    // WHY 2026-07-30 PF-19 deterministic idle presence:
    // Once rotation/jitter are removed, idle still needs one clearly visible
    // slow radial breath. This bounded floor affects only the shader's radial
    // displacement; it does not add speech pulses, random cadence, or halo.
    const stableIdleBreathFloor = speaking ? 0.3 : 0.42;
    particleUniforms.uBreath.value =
      pcmAmplitudeDrive == null
        ? stableIdleBreathFloor +
          bodyBreathForUniforms * 1.06 +
          breathAmp * 0.075 +
          speechDrive * 0.93
        : 0.28 + pcmAmplitudeDrive * 0.82;
    // Traveling surface wave (per-particle dir-dependent, NON-uniform): a faint
    // glisten at idle, a big amplitude-driven wave/wiggle while speaking (convex
    // speechDrive + bass-transient shockwave punch) so the surface ripples as it swells.
    particleUniforms.uWave.value =
      pcmAmplitudeDrive == null
        ? speaking
          ? 0.59 + bodyBreathForUniforms * 0.99 + speechDrive * 1.65 + shockwave * 0.49
          : activeFineMotion
            ? 0.18
            : 0
        : pcmAmplitudeDrive * 2.05 * pcmSpectralAccent;
    // The direction may rotate only while a non-rest state owns fine motion.
    // A fixed rest vector plus zero wave avoids hidden shader travel while the
    // independent uBreath phase keeps the Orb clearly alive.
    tmpWaveDir.set(0, 1, 0);
    (particleUniforms.uWaveDir.value as THREE.Vector3).copy(tmpWaveDir);
    // WHY 2026-06-22/29/30 + 2026-07-31 ORB-DYN-15 active-speaking edge-detail repair:
    // Mobile speech needs per-particle brightness/size contrast so quiet troughs
    // remain crisp instead of becoming diffuse cyan fill. The production
    // trough-detail lift stays intact at the accepted speaking level; the
    // vertex shader gates its continuous `motionTwinkle` phase with
    // uActiveMotion so that contrast can never reintroduce changing idle alpha,
    // RGB, or point size.
    particleUniforms.uTwinkle.value = twinkleForUniforms;
    particleUniforms.uIdleReadabilityLift.value = readableCoreLift;
    // Legacy uniform name is kept for shader/proof compatibility; it now owns
    // bounded particle depth in every state rather than disappearing in speech.
    particleUniforms.uIdleDepthCue.value = particleDepthCue;
    particleUniforms.uRimSpin.value = borderSpinForUniforms;
    particleUniforms.uActiveMotion.value = activeMotionWeight;
    particleUniforms.uScanBlend.value = scanBlend;
    particleUniforms.uScanTravel.value = reducedMotion ? 0.22 : 1;
    particleUniforms.uConnectionMorph.value = thinkingConnectionMorph;
    // Tight per-particle vibration (small travel) ramped by amplitude/rate.
    // WHY 2026-06-20 ORB-PF-60 live-proof correction: the first shader proof
    // compiled but only displaced particles ~0.50 world units, below the visible
    // vibration floor and exactly the "still just spinning" failure. Raise the
    // per-particle buzz to ~1.0 desktop / ~0.7 phone while keeping it far below
    // the rejected broad whole-object shake and leaving object position fixed.
    particleUniforms.uVibration.value =
      pcmAmplitudeDrive == null
        ? vibrationLevel * 1.08
        : pcmAmplitudeDrive * 1.22 * pcmSpectralAccent;
    particleUniforms.uVibRate.value =
      pcmAmplitudeDrive == null
        ? Math.max(1, vibrationRate)
        : 1 + pcmAmplitudeDrive * 3.2 * pcmSpectralAccent;
    // Rapid pulse in/out (frame-rate-independent phase from the channel math).
    particleUniforms.uPulse.value =
      pcmAmplitudeDrive == null
        ? pulseScale * 4.4
        : 0;
    particleUniforms.uPulsePhase.value = pulsePhase;
    // Jump-scale on peaks = the convex "pulse/jump toward the viewer" punch (the
    // Jarvis 5x bass-transient feel). Raised cap; pow(speechLevel,2) makes loud
    // syllables jump much harder. No crop clamp is applied after this scale.
    particleUniforms.uJumpScale.value =
      pcmAmplitudeDrive == null
        ? Math.min(
            0.62,
            shockwave * 0.49 +
              flareLevel * 0.27 +
              Math.pow(clamp01(speechLevel), 2.0) * 0.24,
          )
        : 0;
    // Amplitude radius: idle starts larger; speech swells CONVEXLY so >50%
    // loudness grows the body far more than linearly
    // (exponential read). Bigger look = this geometry scale + wave wiggle, not fill.
    // WHY 2026-07-30 PCM-owned visible extent:
    // Keep one stable body basis across silence and speech, then add raw
    // time-domain amplitude directly. No state switch, smoothed frequency band,
    // spin, camera orbit, transcript or state pulse may resize audible KAI speech.
    particleUniforms.uRadiusScale.value =
      (pcmPeakExtentDrive == null
        ? stablePresenceScale +
          (activeFineMotion ? speechDrive : 0) *
            (OPS_ORB_PEAK_RADIUS_SCALE - OPS_ORB_IDLE_RADIUS_SCALE)
        : opsOrbRadiusScale(renderedOutputLevel)) +
      idlePresenceBreath;
	    // Interior light-from-within on core glow.
    if (pcmPeakExtentDrive != null) {
      // WHY 2026-07-31 shared PCM extent:
      // Radius and glow previously used different thresholds/envelopes, then the
      // DOM bridge sampled glow only every 420 ms. Derive the light-from-within
      // from the exact same output-aligned extent that expands the body so a
      // loud syllable grows and glows together without adding a second pulse.
      coreGlowLevel = clamp01(0.025 + pcmPeakExtentDrive * 0.82);
    }
    particleUniforms.uCoreGlow.value = coreGlowLevel;
    particleUniforms.uMicroTwinkle.value = speaking
      ? microTwinkleForUniforms
      : idleReadableCoreActive
        ? Math.max(microTwinkleLevel, 0.2)
        : microTwinkleLevel * 0.48;
    particleUniforms.uSparkleEnergy.value = speaking
      ? sparkleEnergyForUniforms * 3.4
      : idleReadableCoreActive
        ? Math.max(sparkleEnergy * 0.24, 0.08)
        : sparkleEnergy * 0.24; // WHY 2026-06-22/29/30 ORB-DYN-15 edge-detail/perf repair: amplify speech-only high-frequency glint energy (uSparkleEnergy feeds vGlow's sparkle term + sizePx sparkle variance + the micro-scatter rate ~line 187) so speaking particles resolve as more distinct, more contrasting bright points => more >12 luma-jump edge pixels for the active-speaking detail proof. 2026-06-30 mobile idle readable-core repair: idle gets only a bounded shader sparkle floor on readable core seeds, not broad glow or extra particles.
    particleUniforms.uMicroVariance.value = Math.max(
      0.04,
      idleReadableCoreActive
        ? Math.max(microVarianceForUniforms, 0.1)
        : microVarianceForUniforms,
    );
    // Point size/opacity carry the body density. Speech breath adds soft fill so
    // lowering constant glow does not expose the demoted line accent as the main
    // visual read.
    // WHY 2026-06-21 ORB-PF-55/67 speech-trough density:
    // The perf repair kept draw calls cheap but a quiet mid-speech proof frame
    // dipped just below the point-cloud density floor, making the Orb read like
    // thin lines again. Lift only shader size/opacity while speech is active,
    // not particle count or CPU lines, so trough syllables keep a living particle
    // body without reintroducing phone jank or constant DOM glow.
    // WHY 2026-06-29 mobile particle readability repair:
    // The user rejected the speaking Orb as a big soft blob. Keep the existing
    // amplitude/ripple body, but reduce only speech-time sprite fill so sharp
    // particles stay readable against the glow.
    const speechPointCloudRepair = speaking;
    const speechBodyLift = speaking
      ? visualAmplitudeDrive * 0.24
      : 0;
	    // WHY 2026-06-21 ORB-PF-97 phone-safe fill cap: the bigger speaking LOOK now
	    // comes from radius/wave/jump geometry (above), so CAP point-size growth here.
	    // Oversized soft sprites are the dominant GPU fill/overdraw cost behind the p95
	    // speech spike; the baseline density (ORB-PF-67 trough) is unchanged and only
	    // the loudest-frame peak is bounded.
	    // WHY 2026-06-21 ORB-PF-67/no-crop scale-down: after the user rejected the
	    // oversized Orb and asked for the no-crop body to be scaled down 50%, proof
	    // caught the speech cloud reading too sparse. Add a small shader-only density
	    // lift while speaking; do not raise particle count or line accents.
	    // WHY 2026-06-22 ORB-PF-112 density/perf balance:
	    // Proof 44 showed the room-demand perf hook working, but the reduced sprite
	    // lift made speech fail the point-cloud floor
	    // (maxVisibleParticleRatio=0.0156). Restore density in the shader body only;
	    // CSS bloom is reduced separately, reducing overdraw so the Orb stays
	    // readable without adding more CPU lines, larger crop, or constant glow.
    // WHY 2026-07-01 real-Chrome desktop speaking point-cloud repair:
    // Real Chrome normal-motion proof made the active Orb visibly expand, ripple,
    // vibrate, and shake, but the enlarged desktop point body still topped out at
    // maxVisibleParticleRatio=0.0173 against the protected 0.020 point-cloud floor.
    // Lift only the desktop shader sprite body while speech is active. Do not add
    // particles, raise line/electron caps, touch idle, or weaken the proof gate.
    // WHY [BOL-ORB-PARTICLE-PUMP-002]: a binary speaking lift kept every word at
    // nearly the same sprite size. The same convex envelope that expands the
    // shell now grows the points themselves; quiet windows retain distinct small
    // crimson particles and true crests visibly pump them outward.
    const speechParticleDensityLift = speaking
      ? visualAmplitudeDrive * 1.64
      : 0;
    particleUniforms.uSize.value = Math.min(
      2.85,
		      0.72 +
		      currentSize * 0.25 +
		      bodyBreathForUniforms * 0.08 +
		      shockwave * 0.06 +
          speechParticleDensityLift +
          readableCoreLift * (speaking ? 0.15 : 0.54),
		    );
		    particleUniforms.uOpacity.value = clamp01(
		      0.78 +
		        currentBright * 0.32 +
			      bodyBreathForUniforms * (speaking ? 0.08 : 0.035) +
			        speechBodyLift * 0.62 +
			        (speechPointCloudRepair ? visualAmplitudeDrive * 0.08 : 0) +
            readableCoreLift * (speaking ? 0.14 : 0.26),
		    );
  }

  function writeDebugDataset(): void {
    canvas.dataset.kaiOrbFrame = String(frameCount);
    canvas.dataset.kaiOrbSpeechLevel = speechLevel.toFixed(3);
    canvas.dataset.kaiOrbAudioLevel = audioAmplitudeLevel.toFixed(3);
    canvas.dataset.kaiOrbRenderedOutputLevel = renderedOutputLevel.toFixed(3);
    canvas.dataset.kaiOrbAmplitudeLevel = speechAmplitudeLevel.toFixed(3);
    canvas.dataset.kaiOrbPcmLeaseActive = hasPcmSpeechAuthority()
      ? "true"
      : "false";
    canvas.dataset.kaiOrbPcmSpectralAccent = pcmSpectralAccent.toFixed(4);
    canvas.dataset.kaiOrbPcmPeakExpansion =
      pcmPeakExpansionFactor.toFixed(4);
    canvas.dataset.kaiOrbRadius = currentRadius.toFixed(2);
    canvas.dataset.kaiOrbRadiusScale = String(
      (particleUniforms.uRadiusScale.value as number).toFixed(3),
    );
    canvas.dataset.kaiOrbSpeed = currentSpeed.toFixed(3);
    canvas.dataset.kaiOrbSpinMultiplier = speechSpinMultiplier.toFixed(3);
    canvas.dataset.kaiOrbLineCount = String(visibleLineCount);
    canvas.dataset.kaiOrbElectronCount = String(visibleElectronCount);
    canvas.dataset.kaiOrbShockwave = shockwave.toFixed(3);
    canvas.dataset.kaiOrbLoudnessMultiplier = loudnessMultiplier.toFixed(3);
    canvas.dataset.kaiOrbPulseScale = pulseScale.toFixed(3);
    canvas.dataset.kaiOrbVibration = vibrationLevel.toFixed(3);
    canvas.dataset.kaiOrbVibrationRate = vibrationRate.toFixed(3);
    canvas.dataset.kaiOrbShake = shakeLevel.toFixed(3);
    canvas.dataset.kaiOrbFlare = flareLevel.toFixed(3);
    canvas.dataset.kaiOrbBodyJitter = bodyJitterAmount.toFixed(3);
    // WHY 2026-06-20 ORB-PF-60: body jitter is now per-particle vibration. The
    // X/Y dataset keys are kept for proof compatibility and report the current
    // per-particle vibration displacement magnitude, not a whole-object offset.
    canvas.dataset.kaiOrbBodyJitterX = (particleUniforms.uVibration.value as number).toFixed(2);
    canvas.dataset.kaiOrbBodyJitterY = (particleUniforms.uVibration.value as number).toFixed(2);
	    canvas.dataset.kaiOrbCoreGlow = coreGlowLevel.toFixed(3);
	    canvas.dataset.kaiOrbLineGlow = lineGlowLevel.toFixed(3);
	    canvas.dataset.kaiOrbParticleBreath = particleBreathLevel.toFixed(3);
	    canvas.dataset.kaiOrbMicroTwinkle = microTwinkleLevel.toFixed(3);
	    canvas.dataset.kaiOrbSparkleEnergy = sparkleEnergy.toFixed(3);
    canvas.dataset.kaiOrbMicroVariance = microVarianceLevel.toFixed(3);
    // WHY 2026-07-30 PF-19 mounted camera ownership proof:
    // Source guards can record the fake renderer, but the deployed desktop/mobile
    // gate must also prove the real mounted camera stays centered throughout idle.
    canvas.dataset.kaiOrbCameraX = camera.position.x.toFixed(4);
    canvas.dataset.kaiOrbCameraY = camera.position.y.toFixed(4);
    canvas.dataset.kaiOrbCameraZ = camera.position.z.toFixed(4);
    canvas.dataset.kaiOrbSpinX = spinX.toFixed(4);
    canvas.dataset.kaiOrbSpinY = spinY.toFixed(4);
    canvas.dataset.kaiOrbSpinZ = spinZ.toFixed(4);
    const currentReadableCoreLift = (particleUniforms.uIdleReadabilityLift.value as number).toFixed(3);
    // Legacy key is preserved for existing proof scripts; it now reports the
    // current readable-core lift, including the smaller speech-time lift.
    canvas.dataset.kaiOrbIdleReadabilityLift = currentReadableCoreLift;
    canvas.dataset.kaiOrbReadableCoreLift = currentReadableCoreLift;
    canvas.dataset.kaiOrbIdleDepthCue = String(
      (particleUniforms.uIdleDepthCue.value as number).toFixed(3),
    );
    canvas.dataset.kaiOrbParticleDepthCue = canvas.dataset.kaiOrbIdleDepthCue;
    canvas.dataset.kaiOrbScanBlend = scanBlend.toFixed(3);
    canvas.dataset.kaiOrbScanTravel = String(
      (particleUniforms.uScanTravel.value as number).toFixed(2),
    );
    canvas.dataset.kaiOrbScanScale = String(
      (particleUniforms.uScanScale.value as number).toFixed(2),
    );
    canvas.dataset.kaiOrbConnectionMorph = thinkingConnectionMorph.toFixed(3);
    canvas.dataset.kaiOrbConnectionPattern = String(activeThinkingPatternId);
    canvas.dataset.kaiOrbRimSpin = String((particleUniforms.uRimSpin.value as number).toFixed(3));
    canvas.dataset.kaiOrbReadableCoreSeedCount = String(readableCoreSeedCount);
	    canvas.dataset.kaiOrbParticleCount = String(particleCount);
	    canvas.dataset.kaiOrbPointDiameterScale = OPS_ORB_POINT_DIAMETER_SCALE.toFixed(2);
	    canvas.dataset.kaiOrbBasePointFloorCssPx = OPS_ORB_BASE_POINT_FLOOR_CSS_PX.toFixed(2);
	    canvas.dataset.kaiOrbReadablePointFloorCssPx = OPS_ORB_READABLE_POINT_FLOOR_CSS_PX.toFixed(2);
	    canvas.dataset.kaiOrbPixelRatio = pixelRatio.toFixed(2);
	    canvas.dataset.kaiOrbRendererPath = "canonical-shader";
  }

  function applyBodyTransform(): void {
    // The point shader expands the thinking field in local space. Its CPU-owned
    // relationship rails and electrons must use that same coordinate scale or
    // they collapse into a small unrelated knot at the center of the scan.
    const connectionFieldScale = 1 + scanBlend * (OPS_ORB_SCAN_RADIUS_SCALE - 1);
    particles.rotation.set(spinX, spinY, spinZ);
    particles.position.z = cloudZ;
    lines.rotation.set(spinX, spinY, spinZ);
    lines.position.z = cloudZ;
    lines.scale.setScalar(connectionFieldScale);
    lineGlow.rotation.set(spinX, spinY, spinZ);
    lineGlow.position.z = cloudZ;
    lineGlow.scale.setScalar(connectionFieldScale);
    electronPoints.rotation.set(spinX, spinY, spinZ);
    electronPoints.position.z = cloudZ;
    electronPoints.scale.setScalar(connectionFieldScale);
    core.position.z = cloudZ;
  }

  function clearAmbientIdleLoop(): void {
    if (ambientIdleTimer != null) {
      window.clearTimeout(ambientIdleTimer);
      ambientIdleTimer = null;
    }
  }

  function dampenResidualSpeechChannels(): void {
    // WHY 2026-06-20 ORB-PF-62: with motion in the shader there is no CPU velocity
    // buffer to dampen; hard-reset the speech-owned channels so a stopped reply
    // cannot coast speech motion/glow into the next idle proof window.
    // WHY 2026-06-21 ORB-PF-103 residual-radius reset:
    // Speech can now swell the body much larger. When KAI stops talking, clamp
    // only the leftover radius down to the accepted idle envelope so the next
    // turn does not start from an inflated "idle" baseline that makes real
    // amplitude growth look flat. Idle target/breath still own the final settle.
    // WHY 2026-06-29 ORB idle-radius proof repair:
    // Returning from KAI speech must not leave the next idle baseline near the
    // speaking size. Clamp only the residual radius; the idle target/breath still
    // settle the living particle body and speech pulses can immediately grow it.
    currentRadius = Math.min(currentRadius, 16.2);
    pendingElectronBurst = 0;
    pulseScale = 0;
    vibrationLevel = 0;
    vibrationRate = 1;
    shakeLevel = 0;
    flareLevel = 0;
    bodyJitterAmount = 0;
    glowEnvelope = 0;
	    coreGlowLevel = 0;
	    lineGlowLevel = 0;
	    particleBreathLevel = 0;
	    microTwinkleLevel = 0.04;
	    sparkleEnergy = 0;
	    microVarianceLevel = 0.04;
	    cloudZVelocity *= 0.12;
    coreMat.opacity = 0;
    lineGlowMat.opacity = 0;
  }

  function renderAmbientIdleFrame(): void {
    if (destroyed || paused || !shouldRunAmbientIdleHeartbeat()) return;
    const t = clock.getElapsedTime() + ambientIdleStep * 0.08;
    // WHY 2026-06-13 idle-orb proof repair: a silent Orb still proves as alive
    // between real voice turns. This is the engine's own low-frequency idle breath
    // (not a fake speech cadence) so requestAnimationFrame starvation while the
    // room/speech settle cannot freeze the avatar.
    // WHY 2026-06-21 ORB-PF-103 idle-still: the idle heartbeat keeps the breathing
    // wave/glisten rendering between turns (so RAF starvation can't freeze the
    // avatar) but advances NO rotation — the resting Orb stays a still body with no
    // creeping yaw while KAI is silent. Spin is speech-only.
    setBodyUniforms(t);
    applyBodyTransform();
    updateCoreAndLineGlow(ORB_REFERENCE_FRAME_SECONDS);
    camera.lookAt(0, 0, cloudZ * 0.2);
    renderer.render(scene, camera);
    frameCount += 1;
    writeDebugDataset();
  }

  function ensureAmbientIdleLoop(): void {
    if (destroyed || ambientIdleTimer != null || !shouldRunAmbientIdleHeartbeat()) return;
    const tick = () => {
      ambientIdleTimer = null;
      if (destroyed || !shouldRunAmbientIdleHeartbeat()) return;
      renderAmbientIdleFrame();
      ambientIdleStep = (ambientIdleStep + 1) % 11;
      ambientIdleTimer = window.setTimeout(tick, 760);
    };
    ambientIdleTimer = window.setTimeout(tick, 760);
  }

  // WHY 2026-06-20 ORB-PF-61 amplitude-gated glow:
  // The center sprite and the additive line accent are the in-canvas glow. Both
  // are driven by the amplitude channels with NO constant speaking floor, so the
  // glow visibly swells on loud syllables and settles on quiet/rest. The old
  // 0.42 core floor + always-on speaking base read as "constant glow" and is gone.
  function updateCoreAndLineGlow(dt: number): void {
    const speechVisualActive = isSpeechVisualActive();
    // WHY 2026-06-22 ORB-DYN idle luminosity (RC2): at rest the core sprite used
    // to fade fully dark (targetCoreGlow 0, opacity decaying to 0), so against the
    // bright room the Orb was barely visible. Give the non-speaking core a small
    // BREATHING floor tied to the idle coreGlowLevel set in the animate idle branch
    // so the resting Orb emits a gentle cyan center glow that breathes (not a
    // constant disk) and is clearly dimmer than the speaking core. The idle
    // coreGlowLevel floor is ~0.09-0.13, so this center stays modest and the
    // speaking core (coreGlowLevel up to ~0.8) still out-reads it dramatically.
    const targetCoreGlow = speechVisualActive ? coreGlowLevel : coreGlowLevel * 0.5;
    coreMat.color.lerpColors(coreSpeechColor, brightColor, Math.min(0.2, coreGlowLevel * 0.14 + flareLevel * 0.08));
    // WHY 2026-06-29 mobile particle readability repair:
    // The DOM core now carries the instant peak flash. Keep this in-canvas core as
    // light-from-within, not a broad fog layer; the prior breath-sized sprite filled
    // the canvas and buried the distinct particles the user needs to see.
    coreMat.opacity = speechVisualActive
      ? clamp01(coreGlowLevel * 0.36 + flareLevel * 0.08)
      : clamp01(
          Math.max(
            coreMat.opacity * elapsedFrameDecay(0.82, dt),
            coreGlowLevel * 0.34,
          ),
        );
    const coreScale = 10 + targetCoreGlow * 18 + flareLevel * 8 + particleBreathLevel * 4.5;
    core.scale.set(coreScale, coreScale, 1);

    const thinkingConnectionField = state === "thinking" && !speechVisualActive;
    const targetLineGlow = speechVisualActive
      ? clamp01(pulseScale * 0.7 + vibrationLevel * 0.2 + flareLevel * 0.86 + shockwave * 0.2)
      : thinkingConnectionField
        ? (0.18 + scanBlend * (0.1 + 0.08 * (0.5 + 0.5 * Math.sin(clock.getElapsedTime() * 3.73)))) *
          thinkingConnectionMorph
        : 0;
    lineGlowLevel +=
      (targetLineGlow - lineGlowLevel) *
      elapsedFrameCoefficient(
        targetLineGlow > lineGlowLevel ? 0.76 : 0.22,
        dt,
      );
    lineGlowMat.opacity = speechVisualActive
      ? Math.min(0.07, lineGlowLevel * 0.056)
      : thinkingConnectionField
        ? Math.min(0.022, lineGlowLevel * 0.075)
        : Math.max(0, lineGlowMat.opacity * elapsedFrameDecay(0.72, dt));
  }

	  function animate(): void {
    if (destroyed) return;
    if (paused) return;

	    const t = clock.getElapsedTime();
    // WHY 2026-06-19 dt for amplitude channels: phase accumulators integrate at a
    // fixed Hz regardless of frame rate. Clamped to 50ms so a tab-wake/compositor
    // stall cannot skew the oscillators.
    const dt =
      lastAnimateTime < 0
        ? ORB_REFERENCE_FRAME_SECONDS
        : Math.max(0, Math.min(t - lastAnimateTime, 0.05));
    const frameScale = dt / ORB_REFERENCE_FRAME_SECONDS;
    lastAnimateTime = t;
	    const analyserFrame = readOutputAlignedAnalyserFrame();
    const pcmAuthorityActive = hasPcmSpeechAuthority();
	    const motionAuthority = selectKaiOrbMotionAuthority({
	      analyserPresent: pcmAuthorityActive,
	      contextState: analyser?.context.state ?? null,
	      pcm: analyserFrame,
	    });
    const { bass, mid, treble, level: authoritativeLevel } =
      motionAuthority.frame;
	    // WHY 2026-07-30 time-domain amplitude authority:
	    // Frequency-domain bins are intentionally smoothed by AnalyserNode and can
	    // stay elevated after the audible waveform falls. They may color/detail the
	    // particles, but only current time-domain RMS/peak owns speech extent.
	    const outputGraphConnected =
      analyserTiming === "output" && analyser != null && analyser.context.state === "running";
    const measuredOutputLevel =
      outputGraphConnected && motionAuthority.source === "pcm" ? authoritativeLevel : 0;
    const visualWasActive = isSpeechVisualActive();
    audioAmplitudeLevel = measuredOutputLevel;
    renderedOutputLevel = nextOpsOrbOutputVisualLevel({
      currentLevel: renderedOutputLevel,
      measuredOutputLevel,
      outputConnected: outputGraphConnected,
      elapsedSeconds: dt,
    });
	    const speechActive = isSpeechVisualActive();
    const scanActive = state === "thinking" && !speechActive;
    scanBlend +=
      ((scanActive ? 1 : 0) - scanBlend) *
      elapsedFrameCoefficient(scanActive ? 0.16 : 0.38, dt);
    if (Math.abs(scanBlend - (scanActive ? 1 : 0)) < 0.0005) {
      scanBlend = scanActive ? 1 : 0;
    }
    const targetSpeechLevel = speechActive ? renderedOutputLevel : 0;
    speechAmplitudeLevel = targetSpeechLevel;
    // WHY 2026-07-31 regression-lineage merge:
    // Keep the accepted fast raw-PCM spike plus elapsed-time smoothed release.
    // The rejected phrase envelope/tempo/phase profile averaged that response,
    // globally profiled point size/glow, and added shader/main-thread work. The
    // analyser-smoothed bands already vary by utterance and syllable, so map only
    // their measured spectrum into a bounded ±3% wave/vibration accent. Existing
    // aPhase/aSizeSeed particle seeds keep the response discrete; transcript,
    // random, wall-time, radius, point size, glow, idle, and camera own none of it.
    pcmSpectralAccent =
      outputGraphConnected && measuredOutputLevel > 0.002
        ? 0.97 + clamp01(bass * 0.42 + mid * 0.34 + treble * 0.24) * 0.06
        : 1;
    if (
      visualWasActive &&
      !speechActive &&
      !hasPcmSpeechAuthority() &&
      state !== "speaking"
    ) {
      dampenResidualSpeechChannels();
      activeElectrons.length = 0;
      visibleElectronCount = 0;
      electronGeo.setDrawRange(0, 0);
    }
    speechLevel +=
      (targetSpeechLevel - speechLevel) *
      elapsedFrameCoefficient(
        targetSpeechLevel > speechLevel
          ? 0.86
          : pcmAuthorityActive
            ? 0.42
            : 0.28,
        dt,
      );

    setTargets();
    const speechVisualActiveForRadius = isSpeechVisualActive();
    // WHY 2026-06-21 ORB-PF-79 speech-size repair:
    // Radius needs a faster speech-only attack/release than idle so live syllable
    // peaks read as body growth, while non-speaking idle stays the protected slow
    // breathing Orb.
    // WHY 2026-06-29 mobile amplitude sensitivity repair:
    // Attack and release must both be responsive. A single high floor made the
    // body stay inflated between words; asymmetric radius lerp lets loud peaks
    // punch quickly while quiet gaps visibly shrink before the next syllable.
    const radiusLerp = speechVisualActiveForRadius
      ? targetRadius > currentRadius
        ? 0.22
        : 0.16
      : 0.022;
    const lerp = 0.022;

    currentRadius +=
      (targetRadius - currentRadius) *
      elapsedFrameCoefficient(radiusLerp, dt);
    currentSpeed +=
      (targetSpeed - currentSpeed) *
      elapsedFrameCoefficient(lerp, dt);
    currentBright +=
      (targetBright - currentBright) *
      elapsedFrameCoefficient(lerp, dt);
    currentSize +=
      (targetSize - currentSize) *
      elapsedFrameCoefficient(lerp, dt);
    lineAmount +=
      (targetLineAmount - lineAmount) *
      elapsedFrameCoefficient(lerp, dt);
    electronSpawnRate +=
      (targetElectronRate - electronSpawnRate) *
      elapsedFrameCoefficient(lerp, dt);
    vortexStrength +=
      (targetVortex - vortexStrength) *
      elapsedFrameCoefficient(0.025, dt);
    breathAmp +=
      (targetBreathAmp - breathAmp) *
      elapsedFrameCoefficient(0.025, dt);
    speechSpinMultiplier +=
      (targetSpeechSpinMultiplier - speechSpinMultiplier) *
      elapsedFrameCoefficient(
        targetSpeechSpinMultiplier > speechSpinMultiplier ? 0.2 : 0.075,
        dt,
      );

    if (state !== lastState) {
      transitionEnergy = 0;
      lastState = state;
    }

	    const speechVisualActive = isSpeechVisualActive();
    // WHY 2026-07-30 no-random-spin conversation contract:
    // KAI speech is legible through PCM radius, glow, ripple, and tight
    // per-particle vibration. Reset the production body orientation so an
    // unrelated accumulated rotation can never masquerade as audio response.
    transitionEnergy = 0;
    spinX = 0;
    spinY = 0;
    spinZ = 0;

    const bassJump = Math.max(0, bass - previousBass - 0.04) * 5;
    const nextShockwave = Math.max(
      shockwave *
        elapsedFrameDecay(speechVisualActive ? 0.82 : 0.46, dt),
      bassJump,
	      0,
    );
    shockwave = !speechVisualActive && nextShockwave < 0.035 ? 0 : nextShockwave;
    previousBass = bass;

    // WHY 2026-06-19 amplitude channel computation:
    // Each channel drives a separate perceptual effect with its own nonlinear
    // loudness curve so loud syllables scale as multiples, not just louder versions
    // of the same effect.
    if (speechVisualActive) {
      const rawLevel =
        motionAuthority.source === "pcm"
          ? renderedOutputLevel
          : Math.max(speechLevel, shockwave * 0.55);
      loudnessMultiplier = computeLoudnessMultiplier(rawLevel);
      const lm = loudnessMultiplier;
      const amplitudeRamp = Math.pow(clamp01((rawLevel - 0.06) / 0.78), 1.08);
      const loudnessRateRamp = Math.pow(clamp01((lm - 1) / 11), 1.12);
      const targetVibrationRate = Math.min(3.35, 1 + amplitudeRamp * 1.45 + loudnessRateRamp * 1.18 + shockwave * 0.32);
      vibrationRate +=
        (targetVibrationRate - vibrationRate) *
        elapsedFrameCoefficient(
          targetVibrationRate > vibrationRate ? 0.78 : 0.22,
          dt,
        );

      const pulseHz = 16 + lm * 0.66;
      pulsePhase += pulseHz * dt * Math.PI * 2;
      const targetPulse = Math.max(0, (rawLevel - 0.12) / 0.88) * lm * 0.11;
      pulseScale +=
        (targetPulse - pulseScale) *
        elapsedFrameCoefficient(targetPulse > pulseScale ? 0.8 : 0.26, dt);
      const targetParticleBreath = Math.min(
        1.18,
        0.04 + pulseScale * 1.35 + shockwave * 0.56 + speechLevel * 0.24,
      );
	      particleBreathLevel +=
	        (targetParticleBreath - particleBreathLevel) *
	        elapsedFrameCoefficient(
	          targetParticleBreath > particleBreathLevel ? 0.66 : 0.24,
	          dt,
	        );
	      const targetMicroTwinkle = Math.min(
	        1.28,
	        0.42 +
	          speechLevel * 0.34 +
	          pulseScale * 0.58 +
	          particleBreathLevel * 0.26 +
	          vibrationLevel * 0.18 +
	          shockwave * 0.16,
	      );
	      const targetSparkleEnergy = Math.min(
	        1.32,
	        0.34 +
	          amplitudeRamp * 0.26 +
	          treble * 0.28 +
	          pulseScale * 0.38 +
	          shockwave * 0.42 +
	          flareLevel * 0.18,
	      );
	      const targetMicroVariance = Math.min(
	        1.22,
	        0.42 +
	          speechLevel * 0.28 +
	          particleBreathLevel * 0.2 +
	          vibrationRate * 0.055 +
	          treble * 0.22 +
	          shockwave * 0.15,
	      );
	      microTwinkleLevel +=
	        (targetMicroTwinkle - microTwinkleLevel) *
	        elapsedFrameCoefficient(
	          targetMicroTwinkle > microTwinkleLevel ? 0.74 : 0.22,
	          dt,
	        );
	      sparkleEnergy +=
	        (targetSparkleEnergy - sparkleEnergy) *
	        elapsedFrameCoefficient(
	          targetSparkleEnergy > sparkleEnergy ? 0.78 : 0.2,
	          dt,
	        );
	      microVarianceLevel +=
	        (targetMicroVariance - microVarianceLevel) *
	        elapsedFrameCoefficient(
	          targetMicroVariance > microVarianceLevel ? 0.68 : 0.18,
	          dt,
	        );

	      // WHY 2026-06-29 mobile amplitude sensitivity repair:
	      // Vibration should follow the movement of the amplitude, not sit on a
	      // constant speaking floor. Lower the gate but nearly remove the floor so
	      // quiet syllables flutter and loud syllables buzz hard.
	      const vibThresh = Math.max(0, rawLevel - 0.04);
	      const targetVib = Math.min(
	        0.92,
	        0.025 + Math.pow(vibThresh, 0.9) * lm * 0.48 + amplitudeRamp * 0.12 + particleBreathLevel * 0.04,
	      );
      vibrationLevel +=
        (targetVib - vibrationLevel) *
        elapsedFrameCoefficient(
          targetVib > vibrationLevel ? 0.92 : 0.18,
          dt,
        );

      const shakeTarget = rawLevel > 0.58 ? Math.pow((rawLevel - 0.58) / 0.42, 1.18) * lm * 0.065 : 0;
      shakeLevel +=
        (shakeTarget - shakeLevel) *
        elapsedFrameCoefficient(
          shakeTarget > shakeLevel ? 0.74 : 0.24,
          dt,
        );

      const flareTarget = rawLevel > 0.62 ? Math.pow((rawLevel - 0.62) / 0.38, 1.45) * lm * 0.3 : 0;
      flareLevel +=
        (flareTarget - flareLevel) *
        elapsedFrameCoefficient(
          flareTarget > flareLevel ? 0.9 : 0.12,
          dt,
        );

      const bodyJitterTarget = Math.min(
        0.86,
        vibrationLevel * 0.46 +
        shakeLevel * 0.34 +
        pulseScale * 0.34 +
        flareLevel * 0.18 +
        shockwave * 0.16,
      );
      bodyJitterAmount +=
        (bodyJitterTarget - bodyJitterAmount) *
        elapsedFrameCoefficient(
          bodyJitterTarget > bodyJitterAmount ? 0.86 : 0.28,
          dt,
        );

      // WHY 2026-06-20 ORB-PF-61 amplitude-gated core glow (NO constant floor):
      // Browser proof caught the first shader pass pinning coreGlow near 1.0
      // across quiet and loud buckets. Drive the in-canvas center glow mostly
      // from CURRENT amplitude/burst energy and decay it quickly, while leaving
      // particleBreath/vibration as motion channels instead of glow floors.
      // WHY 2026-06-21 ORB-PF-109 lens-flare glow / smooth glow ramp (not strobe):
      // Keep the glow amplitude-gated and more flash-responsive, but cap the peak
      // around 30% lower than the prior max so the Orb body remains readable. The
      // asymmetric envelope whips on quickly and falls off smoothly; do not return
      // to the earlier constant bloom or hard per-syllable strobe.
      // WHY 2026-06-21 ORB-PF-28 proof repair: canonical proof caught external
      // halo passing while the actual in-canvas center stayed below the light-from-
      // within guard. Boost only amplitude/flare-driven core terms; keep idle/quiet
      // decay intact so this does not become the rejected constant glow.
	      const glowDrive = clamp01(
        Math.pow(clamp01((rawLevel - 0.34) / 0.66), 2.05) * 0.58 + flareLevel * 0.2 + shockwave * 0.05,
      );
		      glowEnvelope +=
            (glowDrive - glowEnvelope) *
            elapsedFrameCoefficient(
              glowDrive > glowEnvelope ? 0.5 : 0.2,
              dt,
            );
		      const coreEnergy = clamp01(0.008 + glowEnvelope * 0.62 + flareLevel * 0.1 + shockwave * 0.035);
      coreGlowLevel +=
        (coreEnergy - coreGlowLevel) *
        elapsedFrameCoefficient(
          coreEnergy > coreGlowLevel ? 0.64 : 0.2,
          dt,
        );
    } else {
      loudnessMultiplier = 1;
      vibrationRate +=
        (1 - vibrationRate) * elapsedFrameCoefficient(0.36, dt);
      pulsePhase += 8 * dt * Math.PI * 2;
      pulseScale *= elapsedFrameDecay(0.88, dt);
      vibrationLevel *= elapsedFrameDecay(0.82, dt);
      shakeLevel *= elapsedFrameDecay(0.78, dt);
      flareLevel *= elapsedFrameDecay(0.72, dt);
      // WHY 2026-06-21 ORB-PF-104: ease the glow envelope back down (smooth whip-off,
      // not an instant cut) while keeping it above zero so glow never hard-snaps off.
      glowEnvelope *= elapsedFrameDecay(0.6, dt);
	      bodyJitterAmount *= elapsedFrameDecay(0.68, dt);
	      particleBreathLevel *= elapsedFrameDecay(0.72, dt);
	      // WHY 2026-06-22 ORB-DYN idle luminosity (RC2): the resting Orb decayed its
	      // core glow to 0, so against the bright room it was barely visible and felt
	      // dead before speech. Ease coreGlowLevel toward a gentle BREATHING cyan
	      // floor instead of decaying to dark, so the Orb stays self-luminous and
	      // alive at rest like the reference video and settles to a small living
	      // sphere after speech. The ~0.085-0.125 range is far below speaking core
	      // glow (so speech still clearly out-reads idle), saturated cyan keeps it
	      // safe from the desaturated white-out guard, and the slow low-amplitude
	      // breath stays under the idle-calm brightChangedPixelRatio budget. This
	      // also feeds the DOM bridge (--kai-orb-core-glow) so the halo/core/spill
	      // layers gain a matching modest idle floor without a constant flat glow.
	      const idleCoreTarget =
	        state === "thinking" ? 0.13 : 0.055 + 0.026 * (0.5 + 0.5 * Math.sin(t * 1.35));
	      coreGlowLevel +=
          (idleCoreTarget - coreGlowLevel) *
          elapsedFrameCoefficient(
            idleCoreTarget > coreGlowLevel ? 0.06 : 0.2,
            dt,
          );
	      const idleMicroTarget = shouldRunAmbientIdleHeartbeat() ? 0.052 : state === "thinking" ? 0.11 : 0.04;
	      microTwinkleLevel +=
          (idleMicroTarget - microTwinkleLevel) *
          elapsedFrameCoefficient(0.08, dt);
	      sparkleEnergy *= elapsedFrameDecay(0.64, dt);
	      microVarianceLevel +=
          (idleMicroTarget * 0.72 - microVarianceLevel) *
          elapsedFrameCoefficient(0.1, dt);
	    }

    // WHY PF-19: a slow sinusoidal z target still translates the whole Orb
    // while every rotation metric says idle. Center rest immediately so only
    // shader radial breathing changes pixels between silent frames.
    cloudZVelocity = 0;
    cloudZ = 0;

    setBodyUniforms(t);
    applyBodyTransform();
    updateConnections(bass, shockwave, t);
    updateElectrons(t, shockwave, frameScale);
    updateCoreAndLineGlow(dt);

    // WHY 2026-07-30 stable conversation camera:
    // Camera orbit and shake changed apparent size independent of PCM. Keep the
    // production conversation camera centered; particle extent is the meter.
    camera.position.x = 0;
    camera.position.y = 0;
    camera.position.z = CAMERA_Z;
    camera.lookAt(0, 0, cloudZ * 0.2);

    renderer.render(scene, camera);
    frameCount += 1;

    if (shaderFailed) {
      // WHY 2026-08-04 canonical shader ownership: a late driver failure must
      // terminate this renderer and surface an explicit error. Keeping the RAF
      // alive or swapping materials recreates a second, wrong Orb.
      destroyed = true;
      canvas.dataset.kaiOrbRendererError = "shader-compile";
      options.onFatalError?.(new Error("KAI Orb canonical shader failed"));
      return;
    }

    if (shouldRunAmbientIdleHeartbeat()) ensureAmbientIdleLoop();
    else clearAmbientIdleLoop();
    if (frameCount % 6 === 0) {
      writeDebugDataset();
    }
    if (options.onAmplitudeFrame) {
      options.onAmplitudeFrame({
        audiblePcm: isAudiblePcmActive(),
        amplitudeLevel: audioAmplitudeLevel,
        loudnessMultiplier,
        pulseScale,
        vibration: vibrationLevel,
        vibrationRate,
        shake: shakeLevel,
        flare: flareLevel,
        bodyJitter: bodyJitterAmount,
	        coreGlow: coreGlowLevel,
	        lineGlow: lineGlowLevel,
	        particleBreath: particleBreathLevel,
	        microTwinkle: microTwinkleLevel,
	        sparkleEnergy,
	        microVariance: microVarianceLevel,
	        bass,
	        mid,
	        treble,
	        rms: analyserFrame.rms,
	        peak: analyserFrame.peak,
	        radiusScale: particleUniforms.uRadiusScale.value as number,
	      });
    }
  }

  resize();
  // Compile before the avatar publishes `data-renderer=webgl`. Failure is
  // explicit and closed; no alternate body may race the canonical one.
  try {
    renderer.compile(scene, camera);
  } catch {
    shaderFailed = true;
  }
  if (shaderFailed) {
    canvas.dataset.kaiOrbRendererError = "shader-compile";
    renderer.forceContextLoss();
    renderer.dispose();
    throw new Error("KAI Orb canonical shader failed");
  }
  canvas.dataset.kaiOrbRendererPath = "canonical-shader";
  canvas.dataset.kaiOrbContextLease = "active";
  animationLease.start();

  return {
    captureFrameDataUrlForProof() {
      // WHY 2026-07-01 Orb proof-owner repair:
      // preserveDrawingBuffer=false can make canvas.toDataURL() return a cleared
      // frame, which forced the proof into fragile page-screenshot color masking.
      // Render the same scene/camera into a temporary transparent target only
      // when the proof hook is called; normal user playback still uses the fast
      // RAF canvas and speech timeout/sustain paths still never render directly.
      const size = renderer.getDrawingBufferSize(new THREE.Vector2());
      const width = Math.max(1, Math.round(size.x));
      const height = Math.max(1, Math.round(size.y));
      const target = new THREE.WebGLRenderTarget(width, height, {
        depthBuffer: false,
        stencilBuffer: false,
      });
      target.texture.colorSpace = THREE.SRGBColorSpace;
      const previousTarget = renderer.getRenderTarget();
      const pixels = new Uint8Array(width * height * 4);
      try {
        renderer.setRenderTarget(target);
        renderer.clear(true, true, true);
        renderer.render(scene, camera);
        renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      } finally {
        renderer.setRenderTarget(previousTarget);
        target.dispose();
      }

      const proofCanvas = document.createElement("canvas");
      proofCanvas.width = width;
      proofCanvas.height = height;
      const context = proofCanvas.getContext("2d");
      if (!context) return canvas.toDataURL("image/png");
      const imageData = context.createImageData(width, height);
      const rowStride = width * 4;
      for (let y = 0; y < height; y += 1) {
        const sourceStart = (height - 1 - y) * rowStride;
        const targetStart = y * rowStride;
        imageData.data.set(pixels.subarray(sourceStart, sourceStart + rowStride), targetStart);
      }
      context.putImageData(imageData, 0, 0);
      canvas.dataset.kaiOrbProofCaptureMode = "offscreen-render-target";
      return proofCanvas.toDataURL("image/png");
    },
    setState(nextState) {
      state = nextState;
      releasePcmSpeechLeaseIfSettled();
      if (
        nextState !== "speaking" &&
        !hasPcmSpeechAuthority() &&
        !isSpeechVisualActive()
      ) {
        // WHY 2026-08-04 canonical audible-motion owner: state transitions may
        // select idle/listening/thinking targets, but only measured output PCM
        // may preserve speech radius, glow, vibration, or particle impulses.
        // Hard-reset those channels here so transcript/boundary/timer timing can
        // neither pre-animate a reply nor leave motion after its audible tail.
        currentSpeed = Math.min(currentSpeed, 0.04);
        vortexStrength = Math.min(vortexStrength, 0.06);
        breathAmp = Math.min(breathAmp, 0.06);
        speechLevel = 0;
        speechAmplitudeLevel = 0;
        shockwave = 0;
        transitionEnergy = Math.min(transitionEnergy, 0.08);
        lineAmount = Math.min(lineAmount, 0.12);
        electronSpawnRate = 0;
        dampenResidualSpeechChannels();
        activeElectrons.length = 0;
        visibleElectronCount = 0;
        electronGeo.setDrawRange(0, 0);
      }
      if (shouldRunAmbientIdleHeartbeat()) ensureAmbientIdleLoop();
      else clearAmbientIdleLoop();
    },
	    setAnalyser(nextAnalyser, timing = "output") {
	      const analyserChanged = analyser !== nextAnalyser;
	      analyser = nextAnalyser;
	      analyserTiming = timing;
	      if (analyserChanged) {
	        pcmPeakExpansionFactor = 1;
	        pcmSpectralAccent = 1;
	        clearAnalyserFrameHistory();
	      }
      if (nextAnalyser && timing === "output") {
        pcmSpeechLeaseActive = true;
        freqData = new Uint8Array(nextAnalyser.frequencyBinCount);
        timeData = new Uint8Array(nextAnalyser.fftSize);
        // A new output graph starts from measured zero, but a still-decaying
        // prior Luna output may crossfade into it. Never reset that visual level
        // at the graph edge; only fresh Aoede samples can raise it.
        speechLevel = 0;
        audioAmplitudeLevel = 0;
        speechAmplitudeLevel = 0;
        pendingElectronBurst = 0;
        transitionEnergy = 0;
        shockwave = 0;
        if (!isSpeechVisualActive()) {
          dampenResidualSpeechChannels();
          currentRadius = 14.6;
          currentSpeed = 0.3;
          currentBright = 0.34;
          currentSize = 0.38;
        }
        speechSpinMultiplier = 1;
        lineAmount = 0;
        electronSpawnRate = 0;
        vortexStrength = 0;
        breathAmp = 0;
        previousBass = 0;
        activeElectrons.length = 0;
        visibleElectronCount = 0;
        electronGeo.setDrawRange(0, 0);
      } else {
        pcmSpeechLeaseActive = false;
        audioAmplitudeLevel = 0;
        speechAmplitudeLevel = 0;
        // WHY 2026-07-30 PF-17 stale-producer handoff:
        // A producer can clear between its final PCM source and React's speech
        // cleanup. Retain zero-PCM authority for that exact speech lifetime so
        // React speech state cannot create a silent tail.
        releasePcmSpeechLeaseIfSettled();
      }
    },
    setPaused(nextPaused) {
      paused = nextPaused;
      animationLease.setPaused(nextPaused);
      if (nextPaused) clearAmbientIdleLoop();
      else if (shouldRunAmbientIdleHeartbeat()) ensureAmbientIdleLoop();
    },
    setReducedMotion(nextReducedMotion) {
      reducedMotion = nextReducedMotion;
    },
    resize,
    destroy() {
      destroyed = true;
      animationLease.destroy();
      clearAmbientIdleLoop();
      scene.remove(particles, lines, lineGlow, electronPoints, core);
      particleGeo.dispose();
      shaderMat.dispose();
      lineGeo.dispose();
      lineMat.dispose();
      // WHY 2026-06-20: lineGlow shares lineGeo (disposed above) — dispose only
      // its material. core is a Sprite (no geometry) — dispose its material+texture.
      lineGlowMat.dispose();
      coreMat.dispose();
      coreTexture.dispose();
      electronGeo.dispose();
      electronMat.dispose();
      particleTexture.dispose();
      electronTexture.dispose();
      // Release the browser's scarce GPU lease as well as Three's JS resources;
      // dispose() alone does not eagerly surrender a WebGL context.
      renderer.forceContextLoss();
      renderer.dispose();
    },
  };
}
